import type { KotSlip } from '@/lib/kotSlip';

// API Response Types
export interface APIResponse<T = any> {
  success: boolean;
  message: string;
  data?: T;
  error?: string;
}

// ── CRM / Customer Directory (Phase 1 marketing loop) ──────────────────
export type LifecycleStage = 'new' | 'active' | 'at_risk' | 'lapsed' | 'vip';

export interface CustomerRow {
  id: string;
  display_name?: string;
  email?: string;
  phone?: string;
  phone_normalized?: string;
  birthday?: string;
  /** 1-12, populated when birthday is set. Lets the directory chip "🎂 Apr" without re-parsing. */
  birthday_month?: number;
  first_visit_at?: string;
  last_visit_at?: string;
  visit_count: number;
  lifetime_spend: number;
  avg_ticket?: number;
  lifecycle_stage?: LifecycleStage;
  /** Loyalty tier key from the nightly recompute (migration 101); absent
   *  until the tenant configures tiers. */
  tier?: string;
  created_at: string;
}

export interface CustomerDetail extends CustomerRow {
  notes?: string;
  /** WhatsApp consent. Opt-out model since 2026-09-04: true by default,
   *  false after the guest replies STOP or staff untick it on the profile. */
  whatsapp_opt_in?: boolean;
}

/**
 * PATCH body for the customer profile inline-edit. Omitted keys are left
 * unchanged on the server; empty-string values clear the column.
 */
export interface UpdateCustomerRequest {
  display_name?: string;
  phone?: string;
  email?: string;
  /** YYYY-MM-DD or empty string to clear. */
  birthday?: string;
  /** Staff-side consent toggle (opt-out model). */
  whatsapp_opt_in?: boolean;
}

/**
 * Returned in `APIResponse.data` alongside a 409 when a phone or email
 * edit collides with another live customer. Lets the UI offer "open that
 * customer instead" rather than just printing an error.
 */
export interface CustomerUpdateConflict {
  field: 'phone' | 'email';
  conflicting_customer_id: string;
  conflicting_customer_name?: string;
}

export interface CustomerListResponse {
  items: CustomerRow[];
  total: number;
  page: number;
  page_size: number;
}

export interface CustomerOrderHistoryItem {
  id: string;
  order_number: string;
  created_at: string;
  completed_at?: string;
  status: string;
  order_type: string;
  item_count: number;
  total_amount: number;
  discount_amount: number;
  payment_method?: 'cash' | 'card' | 'online';
  cancelled_by_credit_note: boolean;
}

export interface CustomerListParams {
  search?: string;
  stage?: LifecycleStage;
  birthday_month?: number | 'current';
  sort?: 'last_visit' | 'lifetime_spend' | 'visit_count' | 'name';
  page?: number;
  page_size?: number;
}

export type SegmentKey =
  | 'birthdays_this_month'
  | 'lapsed_60d'
  | 'at_risk'
  | 'vips'
  | 'first_timers_recent'
  | 'regulars';

export interface CustomerSegmentSummary {
  key: SegmentKey;
  label: string;
  tagline: string;
  count: number;
  /** Member count "as it would have been 30 days ago." Omitted by the
   *  server when a 30-day trend isn't meaningful (calendar-month
   *  segments and lifecycle_stage-based segments). When present, the
   *  trend chip on the card shows (count - count_30d_ago). */
  count_30d_ago?: number | null;
}

export interface CustomerSegmentDetail {
  key: SegmentKey;
  label: string;
  tagline: string;
  items: CustomerRow[];
  count: number;
}

// ── Promo code engine (Phase 1 marketing loop) ────────────────────────
export type PromoDiscountType = 'percent' | 'flat';

// Issuance details for a manager-issued recovery coupon, joined server-side
// from the recovery_coupons ledger. Present only on recovery-coupon promo
// rows; used to render a legible label + an expandable detail popup instead
// of the raw "Recovery·<orderUUID>" description.
export interface RecoveryCouponInfo {
  tier: string;
  reason: string;
  issued_at: string;
  issued_by_name?: string;
  customer_id?: string;
  customer_name?: string;
  customer_phone?: string;
  order_id?: string;
  order_number?: string;
}

export interface PromoCode {
  id: string;
  code: string;
  description: string;
  discount_type: PromoDiscountType;
  discount_value: number;
  min_subtotal?: number;
  valid_from?: string;
  valid_to?: string;
  max_redemptions?: number;
  max_per_customer: number;
  redemption_count: number;
  is_active: boolean;
  campaign_id?: string;
  revenue_attributed_pkr: number;
  created_at: string;
  updated_at: string;
  recovery?: RecoveryCouponInfo | null;
  /** F-MKT-N2: 'bill' = whole bill; 'items' = only the targeted categories / items. */
  scope: PromoScope;
  category_ids: string[];
  product_ids: string[];
  /** "Beverages & Desserts" for an item-scoped code. */
  applies_to?: string;
}

export type PromoScope = 'bill' | 'items';

export interface CreatePromoCodeRequest {
  code: string;
  description?: string;
  discount_type: PromoDiscountType;
  discount_value: number;
  min_subtotal?: number;
  valid_from?: string;
  valid_to?: string;
  max_redemptions?: number;
  max_per_customer?: number;
  campaign_id?: string;
  scope?: PromoScope;
  category_ids?: string[];
  product_ids?: string[];
}

// ── Loyalty programme (Phase 1 marketing loop) ────────────────────────
/** One rung of the loyalty ladder. `key` is the stable identifier stored on
 *  customers.tier and used in segments; `label` is what a guest sees. */
export interface LoyaltyTier {
  key: string;
  label: string;
  min_visits: number;
  perk: string;
}

/** A recurring weekly slot in which each stamp counts for `multiplier`.
 *  `weekdays` uses 0 = Sunday … 6 = Saturday; empty means every day.
 *  `start_hour` is inclusive, `end_hour` exclusive, both business-local;
 *  start === end means all day. */
export interface DoubleStampWindow {
  enabled: boolean;
  weekdays: number[];
  start_hour: number;
  end_hour: number;
  multiplier: number;
}

export interface LoyaltyConfig {
  is_enabled: boolean;
  rule_type: 'visits_to_reward';
  threshold_visits: number;
  reward_label: string;
  reward_discount_type: 'flat' | 'percent';
  reward_discount_value: number;
  /** Earn-side gates. A visit is only earned when the paid order's subtotal is
   *  at least earn_min_subtotal, and not within earn_cooldown_minutes of the
   *  customer's last earned visit. null/undefined = that rule is off. (There is
   *  deliberately no redeem-side floor — an earned reward spends on any order.) */
  earn_min_subtotal?: number | null;
  earn_cooldown_minutes?: number | null;
  /** What a stamp is counted against.
   *  'order' (default) — one stamp per qualifying bill, the historic behaviour.
   *  'item'            — one stamp per qualifying UNIT sold (qty 3 → 3 stamps),
   *                      for tenants running a classic "buy 10 coffees" card.
   *  Absent on configs saved before this field existed; the backend normalises
   *  those to 'order'. */
  earn_basis?: 'order' | 'item';
  /** Caps stamps from a single bill in item mode. null/undefined = uncapped.
   *  Without it a table ordering threshold_visits coffees on one bill completes
   *  a card outright. Ignored (and cleared on save) outside item mode. */
  earn_max_per_order?: number | null;

  // ── Wave 2 growth levers (migration 101) ───────────────────────────────
  // All three are absent on configs saved before they existed, and absent
  // means OFF. Nothing changes for a tenant until its owner opts in.

  /** Tiers rank customers by VISIT COUNT, not spend — a marketing-edition
   *  tenant may have no spend at all. Empty/absent = tiers off. */
  tiers?: LoyaltyTier[];
  /** Count visits within a rolling window instead of over a lifetime.
   *  null = lifetime. A window is what makes a tier losable. */
  tier_window_days?: number | null;
  /** Retire reward PROGRESS after N days. null = stamps never expire, the
   *  behaviour of every tenant before Wave 2. An expired stamp does NOT
   *  un-happen the visit: visit_count and lifetime_spend are untouched. */
  stamp_expiry_days?: number | null;
  /** How many days before expiry to warn. null = no warning. */
  stamp_expiry_nudge_days?: number | null;
  /** Recurring weekly window in which each stamp counts for more. */
  double_stamp?: DoubleStampWindow | null;
  /** Stars-flanked headline printed at the top of the loyalty block on
   *  every customer receipt. Empty = use the auto headline built from
   *  the business name. */
  receipt_headline?: string;
  /** Body line shown to enrolled customers still earning. Supports
   *  {{remaining}}, {{punches}}, {{threshold}}, {{reward}}, {{s}}.
   *  Empty = use the auto copy. The "reward ready" and "save this
   *  visit" variants always use their own auto copy. */
  receipt_progress_message?: string;
  /** Loyalty "card appearance" overrides for the customer-facing scanned
   *  screens (/q/loyalty, /q/wallet) and the Apple/Google Wallet passes.
   *  Each is optional and falls back to the receipt branding (or auto copy,
   *  for headline/subtext) when blank. */
  screen_accent_color?: string;
  /** Page background of the public loyalty pages (/q/join, /q/loyalty,
   *  /p/loyalty). Blank = the default warm cream. Every other colour on
   *  those pages (ink, card, borders, fields, pills) is derived from this
   *  plus the accent server-side, including a neutral inversion when the
   *  chosen background is dark. */
  screen_background_color?: string;
  /** The three wallet-CARD colours. Unlike every screen_* field these do
   *  NOT inherit from the receipt branding: the card is its own surface.
   *  Blank = derive from screen_accent_color exactly as the card did before
   *  the colours were separable, so an un-migrated tenant is unchanged.
   *  screen_accent_color still drives the loyalty web pages and posters. */
  card_background_color?: string;
  card_text_color?: string;
  card_circles_color?: string;
  /** Shop coordinates for the Apple pass's `locations` entry — enables the
   *  "Suggest on Lock Screen" toggle and surfaces the card near the café.
   *  Both-or-neither; absent = no location on the pass. */
  card_latitude?: number;
  card_longitude?: number;
  screen_logo_url?: string;
  /** Logo for the public loyalty WEB pages only (/q/join, /q/loyalty).
   *  screen_logo_url is the Apple/Google Wallet pass mark; blank here means
   *  the pages fall back to it, which is the historic one-logo behaviour. */
  screen_page_logo_url?: string;
  screen_business_name?: string;
  screen_headline?: string;
  screen_subtext?: string;
  /** Unlocks the staff-phone /scan surface (scan a guest's wallet QR from any
   *  staff phone and attach them to an open order). Per-venue, owner-flipped.
   *  Absent on configs saved before this field existed = off. */
  staff_phone_scan_enabled?: boolean;
}

export interface LoyaltyStatus {
  enabled: boolean;
  punches: number;
  threshold: number;
  eligible: boolean;
  reward_label?: string;
  reward_discount_value?: number;
  reward_discount_type?: 'flat' | 'percent';
}

/** Admin rollup for the wallet stamp-card feature: which platforms are
 *  configured (env-gated on the backend) and how many passes are installed. */
export interface WalletStats {
  google_enabled: boolean;
  apple_enabled: boolean;
  /** Google credentials are set but the service-account key failed to parse,
   *  so Google Wallet is off for a fixable reason rather than "never set up". */
  google_configured_but_invalid: boolean;
  google_installs: number;
  apple_installs: number;
  total_installs: number;
  /** Passes we have successfully pushed an update to. `*_installs` counts
   *  rows, which are written the moment a customer taps "Add to Wallet" — so
   *  it counts intent. Only a confirmed push proves a card exists on a phone;
   *  a big gap means people tap the button and don't complete the save. */
  google_confirmed?: number;
  apple_confirmed?: number;
  /** Per-platform breakdown of the last push outcome across live passes. */
  delivery?: PassHealth[];
  /** State of the Google loyalty *class* — the only thing that governs how
   *  cards ALREADY saved on customers' phones look. Absent when Google
   *  Wallet isn't configured. */
  google_class?: GoogleClassSyncStatus;
  /** false = the public origin doesn't resolve to https, so every Apple pass
   *  issued right now ships WITHOUT a webServiceURL: it can never register a
   *  device or receive an update (frozen at install-time balance; no
   *  Automatic Updates / Allow Notifications toggles on the "i" sheet). */
  apple_update_channel_ok?: boolean;
  public_base_url?: string;
}

/** The current wallet broadcast — one marketing message pushed onto every
 *  installed loyalty card (Google class message + Apple pass OFFER field). */
export interface WalletBroadcast {
  message: string;
  sent_at?: string;
  id?: string;
  /** RFC3339. Past this, the message stops being printed onto newly-issued
   *  passes and a worker sweeps it off the installed ones. */
  expires_at?: string;
  sent_by?: string;
  sent_by_name?: string;
  google_class_id?: string;
  history?: WalletBroadcastEntry[];
}

/** One past send, for the audit trail. */
export interface WalletBroadcastEntry {
  id: string;
  message: string;
  sent_at: string;
  sent_by?: string;
  sent_by_name?: string;
  google?: string;
  apple_queued?: number;
  cleared?: boolean;
}

/** Per-platform outcome of one broadcast send. */
export interface WalletBroadcastPlatformResult {
  status: 'sent' | 'queued' | 'cleared' | 'skipped' | 'error' | string;
  reason?: string;
  http_status?: number;
  queued?: number;
  passes?: number;
  confirmed?: number;
}

export interface WalletBroadcastResult {
  broadcast: WalletBroadcast;
  google: WalletBroadcastPlatformResult;
  apple: WalletBroadcastPlatformResult;
  /** true = the message was removed rather than a new one sent. */
  cleared?: boolean;
}

/** How the last push to each live wallet pass went, grouped by platform and
 *  outcome. Every wallet failure so far has been silent — this is where they
 *  become visible without a database session. */
export interface PassHealth {
  platform: 'google' | 'apple' | string;
  /** `never` = saved but no stamp earned since, so nothing has been pushed. */
  status: 'ok' | 'object_missing' | 'no_devices' | 'not_configured' | 'error' | 'never' | string;
  count: number;
  last_error?: string;
}

/** Whether the card branding Google holds matches this restaurant's current
 *  settings. Google creates the loyalty class once and then ignores the class
 *  carried by later save links, so branding edits only reach saved cards when
 *  the backend explicitly pushes (PATCHes) the class. */
export interface GoogleClassSyncStatus {
  enabled: boolean;
  class_id: string;
  tenant_slug: string;
  fingerprint: string;
  /** false = the restaurant has edited its card appearance since the last
   *  successful push, so saved cards still show the old look. */
  in_sync: boolean;
  action?: 'noop' | 'inserted' | 'patched' | 'skipped';
  synced_at?: string;
  last_status?: number;
  last_error?: string;
  logo_url?: string;
  hero_url?: string;
}

/** Counter response for a scanned wallet stamp card (card_token resolved to a
 *  customer). Carries the customer identity so the till can attach them to the
 *  open order via the existing guest flow, plus the same balance/reward shape
 *  as LoyaltyStatus. enabled=false when the programme is off. */
export interface ResolvedLoyaltyCard {
  enabled: boolean;
  customer_id?: string;
  customer_name?: string;
  customer_phone?: string;
  punches: number;
  threshold: number;
  eligible: boolean;
  reward_label?: string;
  reward_discount_value?: number;
  reward_discount_type?: 'flat' | 'percent';
}

/** One row of a customer's loyalty ledger — a punch earned (possibly later
 *  voided by a refund) or a reward redeemed. Returned by the admin
 *  customer-profile loyalty history endpoint. */
export interface CustomerLoyaltyEvent {
  type: 'earned' | 'redeemed';
  at: string;
  order_id: string;
  order_number?: string;
  voided?: boolean;
  /** How many stamps this one earned event was worth (earned rows only).
   *  Absent/0 on rows written before per-item earning — treat as 1. */
  punch_count?: number;
  punches_spent?: number;
  discount_applied?: number;
  /** Where the punch came from (earned rows, admin ledger only):
   *  'order' (paid bill), 'tap' (NFC stamper), 'manual' (admin grant). */
  source?: 'order' | 'tap' | 'manual';
  /** The required reason on manual grants. Admin ledger only. */
  note?: string;
  /** Staff member behind the punch, where known. Admin ledger only. */
  created_by_name?: string;
}

export interface CustomerLoyaltyHistory {
  enabled: boolean;
  balance: number;
  threshold: number;
  reward_label: string;
  events: CustomerLoyaltyEvent[];
}

export interface LoyaltyStats {
  punches_last_30d: number;
  redemptions_last_30d: number;
  total_balances?: number;
  top_balances: Array<{
    id: string;
    display_name?: string;
    phone_normalized?: string;
    balance: number;
    lifetime_spend?: number;
    visit_count?: number;
    last_visit_at?: string;
    lifecycle_stage?: LifecycleStage;
    tier?: string;
  }>;
}

export interface LoyaltyRedeemResult {
  discount_applied: number;
  punches_spent: number;
  reward_label: string;
}

/** Returned by GET /counter/orders/:id/thank-you-promo. Used by the
 *  receipt renderer to draw the "use THANKS-XXXX next visit" block.
 *  When enabled=false the block is skipped entirely. */
export interface OrderThankYouCode {
  enabled: boolean;
  /** Which coupon the receipt should print. "recovery" = a manager-issued
   *  service-recovery coupon (takes precedence); "comeback" = the default
   *  auto-minted code. Drives the receipt block's headline/tone. */
  kind?: 'comeback' | 'recovery';
  code?: string;
  /** Human-ready receipt copy with {{code}} + {{discount}} already
   *  substituted by the server. Display as-is. */
  message?: string;
  /** Short display string, e.g. "10% off" or "Rs. 500 off". */
  discount_label?: string;
  /** ISO date the customer must redeem by, YYYY-MM-DD. */
  expires_on?: string;
  /** Minimum order subtotal (PKR) for the code to apply. 0 = no minimum. */
  min_subtotal_pkr?: number;
}

/** One severity preset for a manager-issued recovery coupon. Percent-only
 *  ("X% off next visit"). Mirrors backend handlers.RecoveryCouponTier. */
export interface RecoveryCouponTier {
  key: string;
  label: string;
  percent: number;
  validity_days: number;
}

// ── Guest feedback & recovery (migration 099) ────────────────────────────

/** Where a piece of feedback sits in the recovery workflow. 'recovered' is
 *  written by the backend when a coupon is actually issued — never by hand. */
export type RecoveryState = 'new' | 'acknowledged' | 'recovered' | 'closed'

/** Tenant settings behind the review booster + receipt QR. Mirrors backend
 *  handlers.reviewConfig (app_settings key marketing.reviews). */
export interface ReviewSettings {
  google_review_url: string
  threshold: number
  gated: boolean
  owner_alert_phone: string
  receipt_qr_enabled: boolean
  /** Print the feedback QR even on slips already carrying the loyalty QR.
   *  Off by default (two codes scan worse) — the tenant's call. */
  receipt_qr_with_loyalty: boolean
  /** Rating at or below which the owner's WhatsApp alert fires. */
  alert_threshold: number
  ask_cooldown_days: number
  receipt_headline: string
  receipt_subline: string
}

/** One row in the guest-recovery inbox. Order context is joined server-side
 *  from the order the review token was minted against, so it is absent on
 *  anonymous poster scans and on ratings that arrived via a WhatsApp link. */
export interface GuestFeedbackRow {
  id: string
  rating: number
  feedback: string
  redirected: boolean
  created_at: string
  /** 'receipt' (per-order token) | 'link' (card token) | 'poster' (anonymous). */
  source: string
  reason_tags: string[]
  recovery_state: RecoveryState
  guest_name: string
  /** Masked to the last 4 digits server-side. */
  guest_phone: string
  /** False for an anonymous rating — there is nobody to send a coupon to, so
   *  the recover action must be disabled rather than failing on submit. */
  has_customer: boolean
  order_number?: string
  /** The bill behind this rating was already refunded by credit note —
   *  a recovery coupon on top of it is a second give. */
  order_credit_noted?: boolean
  order_total?: number
  order_type?: string
  server_name?: string
  coupon_code?: string
  /** Outbox status of the most recent apology to this guest. 'failed' means
   *  the coupon exists but the guest never received it. */
  apology_status?: string
  apology_channel?: string
}

export interface RecoveredFeedback {
  code: string
  discount_label: string
  expires_on: string
  state: RecoveryState
  /** What actually carried the apology. 'whatsapp_click' means nothing was
   *  sent by us — the owner taps wa_deeplink and sends it themselves. */
  message_channel: 'whatsapp_api' | 'email' | 'whatsapp_click' | 'none'
  wa_deeplink?: string
}

/** Reputation Suite settings. Mirrors backend reputation.Config
 *  (app_settings key marketing.reputation). */
export interface ReputationSettings {
  enabled: boolean
  /** The tenant's Google Maps listing: a Maps URL, a place ID, or
   *  "name, city" — passed to the review vendor verbatim. */
  place_query: string
  /** Rating at or below which a NEW public review alerts the owner. */
  alert_threshold: number
  /** Falls back server-side to marketing.reviews.owner_alert_phone. */
  owner_alert_phone: string
  owner_alert_email: string
  /** Business-local hour of the daily poll (0-23). */
  poll_hour: number
}

/** Google Business Profile connection status for the settings card —
 *  never carries the token itself. */
export interface ReputationGoogleStatus {
  /** The Bhookly-wide OAuth client exists on this deployment. */
  configured: boolean
  /** Token + listing stored — the poller can fetch. */
  connected: boolean
  /** Token stored but the account manages several listings — one must be
   *  picked before polling can start. */
  pending_selection: boolean
  location_title: string
}

/** One listing the connected Google account manages. */
export interface ReputationGoogleLocation {
  account: string
  location: string
  title: string
  address?: string
}

/** One public review in the reputation inbox (external_reviews). */
export interface ExternalReviewRow {
  id: string
  source: string
  rating: number
  author_name: string
  author_photo_url?: string
  text: string
  reviewed_at?: string
  first_seen_at: string
  owner_reply_text?: string
  owner_reply_at?: string
  state: RecoveryState
  /** Present only after the owner confirms a customer match — recovery is
   *  disabled without it, since Google gives us only a display name. */
  matched_customer_id?: string
  matched_customer_name?: string
  /** Masked to the last 4 digits server-side. */
  matched_customer_phone?: string
  coupon_code?: string
}

export interface ExternalReviewStats {
  total: number
  avg_rating: number
  /** Reviews at/below the alert threshold still in 'new'/'acknowledged'. */
  open_negative: number
}

/** A fuzzy display-name match candidate for a public review's author. */
export interface ReviewMatchSuggestion {
  id: string
  display_name: string
  phone?: string
  has_email: boolean
  whatsapp_opt_in: boolean
  visit_count: number
  last_visit_at?: string
}

export interface RecoverySummary {
  coupons_issued: number
  coupons_redeemed: number
  /** Tax-inclusive sales on return visits where a recovery coupon was
   *  redeemed. Deliberately NOT called "revenue recovered": some of these
   *  guests would have returned anyway, and the UI must not make a causal
   *  claim we can't defend. */
  revenue_from_recovered: number
  /** What the recovery cost. Never render the revenue without it. */
  discount_given: number
  unresolved_low_ratings: number
}

/** Config behind the counter "Recovery coupon" action. Mirrors backend
 *  handlers.RecoveryCouponConfig (app_settings key recovery_coupon_config). */
export interface RecoveryCouponConfig {
  enabled: boolean;
  tiers: RecoveryCouponTier[];
  /** Max percent a manager may enter as a free-form override. */
  override_cap: number;
  reasons: string[];
  /** Admin-editable receipt copy printed with the recovery code (in place
   *  of the comeback coupon). {{discount}} substitutes to the bare percent.
   *  Blank → backend falls back to its default. */
  receipt_message: string;
}

/** Returned by POST /counter/orders/:id/recovery-coupon — the minted code. */
export interface IssuedRecoveryCoupon {
  code: string;
  discount_label: string;
  percent: number;
  expires_on: string;
  customer_name?: string;
  tier: string;
}

/** Returned by GET /counter/orders/:id/loyalty-claim. Used by receipt
 *  printing to render the per-receipt QR claim block. */
export interface LoyaltyClaimQR {
  enabled: boolean;
  token?: string;
  url?: string;
  /** 6-digit code printed on the receipt so a customer can type it into the
   *  shop-wide claim form (/q/loyalty) to save the exact visit without a QR. */
  claim_code?: string;
  punches: number;
  /** How many stamps THIS bill is worth. Always 1 under the default per-bill
   *  earn basis; the qualifying quantity (post-cap) under per-item earning.
   *  The receipt only prints it when > 1. */
  stamps_earned?: number;
  threshold: number;
  reward_label?: string;
  /** True when the order already has a customer attached at print time
   *  (i.e., the cashier captured the phone). Switches the receipt copy
   *  from "Scan to save this visit" to "Visit saved!" */
  already_attached: boolean;
  /** Identity the order is attached to — present only when
   *  already_attached. The receipt's saved block echoes these back
   *  ("Awarded to Maryam · ····1234") so the guest can verify the visit
   *  landed on the right name / number. Phone is raw; the receipt masks it. */
  customer_name?: string;
  customer_phone?: string;
  /** False when the resolved public URL is localhost / private LAN.
   *  When false, the receipt renderer SKIPS the QR entirely (printing
   *  it would be dead paper — customer phones can't reach the URL). */
  is_scannable: boolean;
  /** Admin-customised stars-flanked headline; empty = receipt
   *  renderer falls back to the auto-built business headline. */
  receipt_headline?: string;
  /** Admin-customised progress copy template (placeholders are
   *  substituted at print time); empty = auto copy. */
  receipt_progress_message?: string;
}

/** Lean row shape returned by GET /counter/customers/search — powers
 *  the cashier-side phone/name autocomplete. */
export interface CustomerQuickLookup {
  id: string;
  display_name?: string;
  phone?: string;
  phone_normalized?: string;
  visit_count: number;
  /** Saved default delivery address, used to pre-fill the counter delivery field. */
  default_address?: string;
  /** All saved addresses (default first, capped at 5) — powers the
   *  tap-to-switch chips on delivery orders. */
  addresses?: CustomerQuickAddress[];
}

/** One saved address in the counter autocomplete payload. */
export interface CustomerQuickAddress {
  id: string;
  label?: string;
  address: string;
  is_default: boolean;
}

/** Full saved-address row (admin address book, F-FE-N30). Mirrors the
 *  storefront's customer_addresses shape — same table, same endpoints core. */
export interface CustomerAddress {
  id: string;
  label: string;
  address: string;
  is_default: boolean;
  /** Pinned map location — optional; legacy addresses are text-only. */
  latitude?: number;
  longitude?: number;
  place_id?: string;
}

/** Add/update payload for a customer address. */
export interface CustomerAddressRequest {
  label: string;
  address: string;
  latitude?: number;
  longitude?: number;
  place_id?: string;
}

/**
 * Customer-facing base URL used by loyalty QRs, marketing QR codes,
 * and any other public-facing surface the system generates. Read /
 * written via `/admin/settings/public-links`.
 *
 *  • `public_base_url` — what's stored in the DB (empty when not set)
 *  • `resolved.url` — what the system actually uses right now
 *  • `resolved.source` — where that value came from (env > db > auto)
 *  • `resolved.is_scannable` — false when the URL is localhost or a
 *    private LAN range (a customer's phone can't reach those)
 */
// F-FE-N195 — GET /admin/storefront-app/stats.
export interface StorefrontAppInstallDay {
  day: string // YYYY-MM-DD; empty on the all-time total
  android: number
  ios: number
  other: number
}
export interface StorefrontAppStats {
  days: number
  installs_by_day: StorefrontAppInstallDay[]
  installs_total: StorefrontAppInstallDay
  installs_window: number
  active_devices: number
  orders_app: number
  orders_web: number
  push_subscribers: number
  tracking_since?: string
}

export interface PublicLinksConfig {
  public_base_url: string;
  resolved: {
    url: string;
    source: 'env' | 'db' | 'auto';
    is_scannable: boolean;
  };
  /**
   * Where the customer storefront lives — a DIFFERENT origin from `resolved`,
   * which is the POS host that serves /q/. A table QR encodes the POS host and
   * redirects to this one, so an empty `url` here means a printed code has
   * nowhere to send a diner. Optional so an older backend reads as unset.
   */
  ordering?: {
    url: string;
    source: 'env' | 'db' | 'auto';
    is_scannable: boolean;
  };
}

/** Snapshot of a promo currently applied to an open order. */
export interface AppliedPromoState {
  applied: boolean;
  promo_code_id?: string;
  code?: string;
  description?: string;
  /** Cashier-facing legible label. For recovery/comeback coupons this
   *  replaces the raw description marker; for ordinary promos it equals it. */
  label?: string;
  discount_applied?: number;
  /** F-MKT-N2: what an item-scoped code covers; empty = whole bill. */
  applies_to?: string;
}

/** Bank card promo — admin catalog row ("20% off with HBL Platinum"). */
export interface CardPromo {
  id: string;
  bank_name: string;
  card_label: string;
  discount_percent: number;
  max_discount_amount?: number;
  min_bill_amount?: number;
  /** Go time.Weekday numbering: 0=Sunday … 6=Saturday. */
  days_of_week: number[];
  valid_from?: string;
  valid_to?: string;
  is_active: boolean;
  redemption_count: number;
  /** Ledgered discounts on non-credit-noted orders — expected bank reimbursement. */
  discount_attributed_pkr: number;
  created_at: string;
  updated_at: string;
}

export interface CreateCardPromoRequest {
  bank_name: string;
  card_label: string;
  discount_percent: number;
  max_discount_amount?: number;
  min_bill_amount?: number;
  days_of_week?: number[];
  valid_from?: string;
  valid_to?: string;
}

export interface UpdateCardPromoRequest {
  bank_name?: string;
  card_label?: string;
  discount_percent?: number;
  max_discount_amount?: number;
  clear_max_discount?: boolean;
  min_bill_amount?: number;
  clear_min_bill?: boolean;
  days_of_week?: number[];
  valid_from?: string;
  clear_valid_from?: boolean;
  valid_to?: string;
  clear_valid_to?: boolean;
  is_active?: boolean;
}

/** Quick-pick chip: a card promo valid right now for a given order. */
export interface EligibleCardPromo {
  id: string;
  bank_name: string;
  card_label: string;
  discount_percent: number;
  max_discount_amount?: number;
  /** Server-computed min(round2(subtotal × pct), cap). */
  projected_discount: number;
}

/** Snapshot of the card promo currently applied to an open order. */
export interface AppliedCardPromoState {
  applied: boolean;
  card_promo_id?: string;
  bank_name?: string;
  card_label?: string;
  discount_percent?: number;
  discount_applied?: number;
  /** Receipt/UI label, server-built: "HBL Card Discount (20%)". */
  label?: string;
}

/** One order row inside the per-bank card-promo claim report. */
export interface CardPromoClaimRow {
  order_id: string;
  order_number: string;
  business_date_label: string;
  card_label: string;
  discount_percent: number;
  order_total: number;
  discount_applied: number;
  /** Completed and not credit-noted — counted in claim totals. */
  claimable: boolean;
  excluded_reason?: 'refunded' | 'not_completed';
}

export interface CardPromoClaimBank {
  bank_name: string;
  order_count: number;
  claim_total: number;
  rows: CardPromoClaimRow[];
}

export interface CardPromoClaimReport {
  from_label: string;
  to_label: string;
  banks: CardPromoClaimBank[];
  grand_total: number;
  total_orders: number;
  excluded_count: number;
}

/** Successful apply-promo response. */
export interface AppliedPromoResult {
  discount_applied: number;
  code: string;
  promo_code_id: string;
  description: string;
  /** Cashier-facing legible label — see AppliedPromoState.label. */
  label?: string;
  applies_to?: string;
}

export interface UpdatePromoCodeRequest {
  code?: string;
  description?: string;
  discount_type?: PromoDiscountType;
  discount_value?: number;
  min_subtotal?: number;
  clear_min_subtotal?: boolean;
  valid_from?: string;
  valid_to?: string;
  clear_valid_from?: boolean;
  clear_valid_to?: boolean;
  max_redemptions?: number;
  clear_max_redemptions?: boolean;
  max_per_customer?: number;
  is_active?: boolean;
  campaign_id?: string;
  clear_campaign?: boolean;
  /** When sent, the target set is replaced by category_ids + product_ids. */
  scope?: PromoScope;
  category_ids?: string[];
  product_ids?: string[];
}

// ── Gift cards (Wave 2, ledger-only) ──────────────────────────────────
// `balance` is computed server-side from the append-only ledger, never
// stored — do not cache it anywhere it could be shown as authoritative
// after a redemption elsewhere.
export interface GiftCardTx {
  id: string;
  delta: number;
  balance_after: number;
  note: string;
  created_at: string;
  created_by_name: string;
}

export interface GiftCard {
  id: string;
  code: string;
  customer_id?: string;
  customer_name: string;
  customer_phone: string;
  initial_amount: number;
  balance: number;
  status: 'active' | 'void';
  /** Server-computed, not re-derived per client: the list and the liability
   *  tile disagreed once already about whether an expired card is "live". */
  expired: boolean;
  redeemable: boolean;
  note: string;
  expires_at?: string;
  created_at: string;
  issued_by_name: string;
  transactions?: GiftCardTx[];
}

export interface GiftCardListResponse {
  gift_cards: GiftCard[];
  /** Value guests can still walk in and spend: excludes void AND expired. */
  outstanding_liability: number;
  outstanding_count: number;
}

// ── Campaigns (Phase 1 marketing loop) ────────────────────────────────
export type CampaignStatus = 'draft' | 'active' | 'paused' | 'archived';
export type CampaignChannel = 'email' | 'whatsapp_click' | 'whatsapp_api' | 'sms';

export interface Campaign {
  id: string;
  slug?: string;
  name: string;
  description: string;
  status: CampaignStatus;
  channel_set: CampaignChannel[];
  segment_def: Record<string, unknown>;
  promo_code_id?: string;
  promo_code?: string;
  message_subject: string;
  message_body: string;
  created_at: string;
  updated_at: string;
  sent_at?: string;
  wa_template_name?: string;
  wa_template_params: string[];
  mint_per_customer: boolean;
  sent_count: number;
  opened_count: number;
  clicked_count: number;
  redeemed_count: number;
  revenue_attributed_pkr: number;
}

/** One entry of the code-shipped WhatsApp template registry (one Bhookly
 *  WABA, centrally approved — see backend messaging/wa_templates.go). */
export interface WATemplate {
  name: string;
  lang: string;
  param_names: string[];
  purpose: 'campaign' | 'automation' | 'digest' | 'alert' | 'order';
  preview: string;
}

export interface CreateCampaignRequest {
  name: string;
  description?: string;
  status?: CampaignStatus;
  channel_set?: CampaignChannel[];
  segment_def?: Record<string, unknown>;
  promo_code_id?: string;
  message_subject?: string;
  message_body?: string;
  wa_template_name?: string;
  wa_template_params?: string[];
  mint_per_customer?: boolean;
}

export interface UpdateCampaignRequest {
  name?: string;
  description?: string;
  status?: CampaignStatus;
  channel_set?: CampaignChannel[];
  segment_def?: Record<string, unknown>;
  promo_code_id?: string;
  clear_promo?: boolean;
  message_subject?: string;
  message_body?: string;
  wa_template_name?: string;
  wa_template_params?: string[];
  mint_per_customer?: boolean;
}

export interface CampaignRecipient {
  customer_id: string;
  display_name?: string;
  email?: string;
  phone_e164?: string;
  rendered_subject: string;
  rendered_body: string;
  wa_me_url?: string;
  already_sent_in_last_30d: boolean;
}

export interface CampaignRecipientsResponse {
  campaign_name: string;
  recipients: CampaignRecipient[];
  count: number;
}

export interface CampaignPreviewResponse {
  subject: string;
  body: string;
  wa_me_url?: string;
  warnings: string[];
  sample_recipient: { name?: string; email?: string; phone?: string };
}

export interface CustomerDuplicateCandidate {
  id: string;
  display_name?: string;
  email?: string;
  phone?: string;
  phone_normalized?: string;
  visit_count: number;
  lifetime_spend: number;
  last_visit_at?: string;
  /** Server-rendered explanation ("Same phone", "Similar name + email", ...). */
  match_reason: string;
  /** Which fields actually triggered the match. Empty/undefined on older payloads. */
  match_fields?: ('name' | 'phone' | 'email')[];
  /** Fields where the candidate has a value that disagrees with the source. */
  conflicts?: ('phone' | 'email')[];
}

export interface PaginatedResponse<T = any> {
  success: boolean;
  message: string;
  data: T;
  meta: MetaData;
}

export interface MetaData {
  current_page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

// User Types
export type ThemePreference = 'light' | 'dark' | 'high-contrast';

export interface User {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  role: 'admin' | 'manager' | 'inventory_manager' | 'counter' | 'server' | 'kitchen' | 'accountant' | 'kitchen_manager' | 'rider';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  /** HTTPS image URL or data:image/* from a small local upload */
  profile_image_url?: string | null;
  /** UI theme preference persisted server-side so it follows the user across POS stations. */
  theme_preference?: ThemePreference;
  /**
   * Opt-in: receives the day-close Z report by email after every Day Close
   * (F-RPT-N16). Set from the staff create/edit form.
   */
  email_reports_enabled?: boolean;
  /**
   * WhatsApp number for the day-close Z report (F-RPT-N40). The API's SELECT
   * does `COALESCE(whatsapp_phone, '')`, so an unset number comes back as
   * `""`, never `null` — treat `""` as "no number".
   */
  whatsapp_phone?: string;
  /**
   * Opt-in: receives the day-close Z report over WhatsApp after every Day
   * Close (F-RPT-N40). Separate from `whatsapp_phone` so a number can be on
   * file with this off (a paused subscription).
   */
  whatsapp_reports_enabled?: boolean;
  /** F-MM-N31: this person's open meal allowance, or null (admin users list only). */
  meal_allowance?: StaffMealAllowanceSummary | null;
}

/** One staff login's open meal allowance, as GET /admin/users returns it. */
export interface StaffMealAllowanceSummary {
  account_id: string;
  status: 'active' | 'suspended';
  monthly_allowance: number;
  used_this_month: number;
  over_this_month: number;
  /** YYYY-MM */
  cycle_month: string;
}

/** username may be the staff username or their email (same JSON field for backward compatibility). */
export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  token: string;
  user: User;
}

// Category Types
export interface Category {
  id: string;
  name: string;
  description?: string;
  color?: string;
  /** Storefront banner (migration 111): data URI or https URL. Shown above the category on the online menu. */
  image_url?: string | null;
  sort_order: number;
  is_active: boolean;
  /** Default PCT/HS for new products in this category (FBR). */
  default_pct_code?: string;
  created_at: string;
  updated_at: string;
  /** Kitchen KOT routing — from category_station_map */
  kitchen_station_id?: string | null;
  kitchen_station_name?: string | null;
  kitchen_station_output_type?: 'kds' | 'printer' | null;
  /** Number of menu products with this category_id */
  product_count?: number;
}

/**
 * A Tag is an overlapping, merchant-defined label applied to products purely
 * for reporting segments (e.g. a "Summer Special" promo). Unlike Category (one
 * authoritative bucket per product), a product may carry many tags, and tags
 * never enter category/item sales aggregation — they only power the Tag
 * grouping/filter in the menu-performance report.
 */
export interface Tag {
  id: string;
  name: string;
  color?: string | null;
  description?: string | null;
  is_active: boolean;
  sort_order: number;
  /** Opt-in (migration 111): render this tag as a small badge on the online menu item. */
  show_on_storefront?: boolean;
  created_at: string;
  updated_at: string;
  /** Number of products linked via product_tags. */
  product_count?: number;
}

// Product Types
export type ProjectedCostBasis = 'amount' | 'percent';

/** One recipe component (migrations/035): a stock item drawn down per sale. */
export interface ProductStockComponentInput {
  stock_item_id: string;
  /** Quantity drawn down per sale, expressed in the stock item's unit. */
  deduct_qty: number;
  /** Unit the operator entered the quantity in (converts to the stock unit). */
  deduct_unit?: string | null;
  /** What the operator TYPED, in deduct_unit — the sale engine converts it
   *  exactly at sale time (12 × "1 each" → 1.00000 dozen), killing the
   *  stored-reciprocal drift (F-BE-N58). */
  deduct_qty_entered?: number | null;
  /** F-MOD-N11: 'all' (default) | 'dine_in' | 'takeaway'. */
  order_type_scope?: 'all' | 'dine_in' | 'takeaway';
}

/** One authored recipe row as the Costing & Recipes sheet edits it (F-MOD-N11). */
export interface RecipeComponentView {
  stock_item_id: string;
  name: string;
  unit: string;
  deduct_qty: number;
  deduct_unit: string | null;
  deduct_qty_entered: number | null;
  order_type_scope: 'all' | 'dine_in' | 'takeaway';
  /** Option rows only: 'add' | 'replace'. */
  mode?: string;
  position: number;
  /** COALESCE(wac, default) — the rate the recipe prices at. null = uncosted. */
  unit_cost: number | null;
  line_cost: number;
}

/** One modifier option on a group attached to a product, with its own recipe. */
export interface RecipeVariant {
  group_id: string;
  group_name: string;
  option_id: string;
  option_name: string;
  price_delta: number;
  price_mode: 'add' | 'replace';
  is_default: boolean;
  recipe_multiplier: number;
  has_recipe: boolean;
  components: RecipeComponentView[];
  /** Dine-in per-serving cost WITH this option chosen; null when uncosted. */
  cost: number | null;
  uncosted: number;
  /** How many products carry this option's group — everyone its recipe changes. */
  group_product_count: number;
  /** F-MOD-N13: this item has its own recipe for the option (components are those rows). */
  own_recipe?: boolean;
  /** F-MOD-N14: items this option swaps on the line (shared). */
  swaps?: OptionSwap[];
}

export interface ProductRecipe {
  product_id: string;
  name: string;
  price: number;
  recipe_yield: number;
  projected_cost: number | null;
  projected_cost_basis: string;
  components: RecipeComponentView[];
  /** Dine-in per-serving base cost under the checkout completeness rule; null when uncosted. */
  recipe_cost: number | null;
  uncosted: number;
  variants: RecipeVariant[];
}

export interface OptionRecipe {
  option_id: string;
  option_name: string;
  group_name: string;
  recipe_multiplier: number;
  components: RecipeComponentView[];
  /** Highest effective price among products carrying the group (row guard cap); null when none. */
  max_item_price: number | null;
  /** How many products carry the option's group. */
  product_count: number;
  /** F-MOD-N13: present on a product-scoped read. */
  product_id?: string;
  /** F-MOD-N13: true when the product has its own recipe for this option. */
  own_recipe?: boolean;
  /** F-MOD-N14: items this option swaps on the line (shared). */
  swaps?: OptionSwap[];
  /** F-MOD-N14: false until the server has the swaps table. */
  swaps_supported?: boolean;
}

/** F-MOD-N14: on a line with the option, draw `to` wherever the recipe draws `from`. */
export interface OptionSwap {
  from_stock_item_id: string;
  from_name?: string;
  from_unit?: string;
  to_stock_item_id: string;
  to_name: string;
  to_unit: string;
}

/** F-MOD-N13: one option on one product, as the admin edits it. */
export interface ProductOptionOverride {
  option_id: string;
  option_name: string;
  group_id: string;
  group_name: string;
  price_mode: 'add' | 'replace';
  shared_price_delta: number;
  shared_cost_delta: number | null;
  shared_is_available: boolean;
  /** null = this product uses the shared value. */
  price_delta: number | null;
  cost_delta: number | null;
  is_available: boolean | null;
  has_own_recipe: boolean;
  group_product_count: number;
}

export interface StockItemUsage {
  kind: 'product' | 'option';
  product_id?: string;
  product_name?: string;
  option_id?: string;
  option_name?: string;
  group_name?: string;
  deduct_qty: number;
  order_type_scope: 'all' | 'dine_in' | 'takeaway';
}

/**
 * Which rung of the sale engine's cost ladder actually produced a product's
 * COGS. Mirrors resolveItemUnitCost (backend/internal/handlers/stock_sale.go):
 * a consignment vendor's share of the sale price, else the recipe summed at
 * moving-average cost, else the operator's projected_cost, else uncosted.
 */
export type EffectiveCostSource = 'consignment' | 'recipe' | 'projected' | 'none';

/**
 * Per-product costing health (F-RPT-N25) — what checkout would really charge to
 * COGS, and the signals that explain it. Lets Menu Costing show a recipe that
 * silently undercuts the operator's own figure instead of hiding it.
 */
export interface ProductCostingHealth {
  product_id: string;
  name: string;
  price: number;
  /** The operator's manually-set cost, if any. */
  projected_cost: number | null;
  /** Recipe summed at each component's moving-average cost. 0 when no recipe. */
  recipe_cost: number;
  component_count: number;
  /** Components resolving to zero cost — these silently understate COGS. */
  components_missing_cost: number;
  /** Components costed off a typed-in default rather than a real receipt. */
  components_without_wac: number;
  /** Cost one unit sold today would snapshot. Null only when uncosted. */
  effective_cost: number | null;
  effective_cost_source: EffectiveCostSource;
}

/**
 * One extra product photo (storefront hover float). `url` is http(s) when
 * read back; on save it may also be a data:image URI the backend rehosts.
 * `cutout_url` is a transparent version made offline — never edited in the
 * admin, only preserved.
 */
export interface ProductGalleryImage {
  url: string;
  cutout_url?: string | null;
}

export interface Product {
  id: string;
  category_id?: string;
  name: string;
  description?: string;
  price: number;
  image_url?: string;
  /** Transparent version of image_url, made offline. Read-only in the admin. */
  image_cutout_url?: string | null;
  /** Up to 3 extra photos, in display order (storefront hover float). */
  gallery_images?: ProductGalleryImage[] | null;
  barcode?: string;
  sku?: string;
  is_available: boolean;
  /**
   * Independent online-ordering gate (migration 068). Defaults to true. An
   * item can be is_available=true (sellable at the counter) yet
   * is_storefront_visible=false (hidden from the public storefront menu).
   * Only meaningful for tenants with online ordering enabled.
   */
  is_storefront_visible?: boolean;
  preparation_time: number;
  sort_order: number;
  /** FBR / PRA PCT (HS) code for fiscal line items */
  pct_code?: string;
  fbr_uom?: string;
  fbr_sale_type?: string;
  fbr_sro_schedule_no?: string;
  fbr_sro_item_serial_no?: string;
  fbr_fed_payable?: number;
  /**
   * Optional projected/assumed cost per unit (PKR). NULL when not set —
   * those rows are excluded from Projected Gross Margin math (the UI
   * surfaces coverage % so the metric stays honest).
   *
   * Canonical persisted shape:
   *  - `projected_cost` is always the amount used in math.
   *  - `projected_cost_basis` ('amount' | 'percent') tells the form which
   *    input mode the user picked.
   *  - `projected_cost_percent` is only populated for basis='percent' and
   *    lets the form (or the server) re-derive `projected_cost` from
   *    `price * percent / 100` whenever price changes.
   */
  projected_cost?: number | null;
  projected_cost_basis?: ProjectedCostBasis;
  projected_cost_percent?: number | null;
  /**
   * Opt-in retail-goods inventory tracking (migrations/034). null =
   * untracked (default) — the manual store flow is unaffected. When set to a
   * stock_items.id, selling this product decrements that stock item by
   * `stock_deduct_qty` per unit sold, with reversal on void/credit-note/cancel.
   */
  tracked_stock_item_id?: string | null;
  stock_deduct_qty?: number;
  /**
   * Lightweight recipe builder (migrations/035): the stock items this product
   * draws down per unit sold, each with its own ratio. Supersedes the 1:1
   * scalar fields above (which mirror the first component). Hydrated lazily by
   * the product form via getProductStockComponents — not returned on list reads.
   */
  components?: ProductStockComponentInput[];
  created_at: string;
  updated_at: string;
  category?: Category;
  /** Overlapping reporting labels (product_tags). Populated on read. */
  tags?: Tag[];
  /** Tag ids the product carries; sent on write to replace its tag set. */
  tag_ids?: string[];
}

/** FBR / PRAL fiscal sync state on an order (orders.fiscal_details) */
export interface FiscalPartyPrintBlock {
  name?: string;
  ntn_cnic?: string;
  province?: string;
  address?: string;
  registration_type?: string;
}

export interface FiscalInvoiceLinePrint {
  item_s_no: number;
  hs_code: string;
  product_description: string;
  uo_m: string;
  quantity: number;
  value_sales_excluding_st: number;
  rate: string;
  sales_tax_applicable: number;
  fixed_notified_value_or_retail_price: number;
  discount: number;
  total_values: number;
  sale_type: string;
  sro_schedule_no: string;
  sro_item_serial_no: string;
  fed_payable: number;
  extra_tax?: string;
  fbr_line_invoice_no?: string;
}

export interface FiscalInvoicePrintData {
  invoice_type: string;
  invoice_date: string;
  scenario_id?: string;
  invoice_ref_no?: string;
  fbr_posted_at?: string;
  seller: FiscalPartyPrintBlock;
  buyer: FiscalPartyPrintBlock;
  lines: FiscalInvoiceLinePrint[];
  totals_value_excl_st: number;
  totals_sales_tax: number;
  totals_grand: number;
}

export interface FiscalDetails {
  status: string;
  irn?: string;
  qr_code_value?: string;
  /** PNG bitmap (base64) for receipt printing; populated when FBR/PRAL POS sync succeeds */
  qr_code_png_base64?: string;
  fiscal_usin?: string;
  fiscal_workflow?: string;
  last_sync_attempt?: string;
  error_log?: string;
  authority?: string;
  raw_response?: string;
  /** Mirror of posted FBR DI payload for slips (after successful sync). */
  invoice_print?: FiscalInvoicePrintData;
  /** LOCAL_BRIDGE: pre-built PRAL POS JSON for the frontend to relay to localhost IMSFiscal. */
  local_payload?: string;
}

export interface FiscalConfigPublic {
  authority: string;
  pos_id: string;
  ntn: string;
  is_sandbox: boolean;
  strn?: string;
  pntn?: string;
  pos_registration_number?: string;
  sfd_proxy_url?: string;
  /** PRAL-designated POS integration POST URL */
  pos_post_url?: string;
  /** Effective POST URL when pos_post_url is empty (sandbox default or PRAL_POS_POST_URL on server). */
  resolved_pos_post_url?: string;
  /** Optional FBR DI validate URL override */
  fbr_validate_url?: string;
  /** Optional FBR DI post URL override */
  fbr_post_url?: string;
  seller_business_name?: string;
  seller_province?: string;
  seller_address?: string;
  scenario_id?: string;
  buyer_registration_default?: string;
  api_key_set: boolean;
  api_key_masked?: string;
}

export interface FiscalTestConnectionResult {
  ok: boolean;
  irn: string;
  qr_code_value: string;
  authority: string;
  invoice_print?: FiscalInvoicePrintData;
  raw?: string;
  error?: string;
  is_sandbox?: boolean;
  response_code?: string;
  response_message?: string;
  post_url?: string;
  /** Exact PRAL POS JSON POST body (truncated); esp PostData uses lowercase `items` array key. */
  request_preview?: string;
}

/** Admin-only: SN002 DI validate sandbox checklist (FBR + sandbox + Bearer saved). */
export interface FiscalDiSn002SandboxValidateResult {
  ok: boolean;
  http_status: number;
  validate_url?: string;
  request_preview?: string;
  response_body?: string;
  error?: string;
  validation_parent_status?: string;
  response_invoice_number?: string;
  /** Persisted STRN (trimmed) sent as sellerNTNCNIC after digit normalization. */
  seller_registration?: {
    source: string;
    raw: string;
    normalized: string;
  };
}

export interface DiningTable {
  id: string;
  table_number: string;
  seating_capacity: number;
  location?: string;
  zone?: string;
  is_occupied: boolean;
  has_active_order?: boolean;
  map_x?: number;
  map_y?: number;
  map_w?: number;
  map_h?: number;
  map_rotation?: number;
  shape?: 'rectangle' | 'square' | 'round' | string;
  created_at: string;
  updated_at: string;
  /**
   * Rotatable slug behind this table's dine-in QR (F-BE-N52). Null/absent
   * until an admin first generates a code — slugs are minted lazily, never
   * backfilled, so a restaurant that has never printed a table code has none.
   */
  qr_slug?: string | null;
  /** Latest order created_at for this table (non-cancelled orders); server-computed */
  last_booked_at?: string | null;
  /**
   * The table's current open dine-in order, when occupied. Emitted by
   * GET /tables (handlers/tables.go) via a LATERAL join — powers the live
   * floor tiles (order #, guests, seated timer, running total) and the
   * server action rail. Null/absent when the table is free.
   */
  current_order?: {
    id: string;
    order_number: string;
    customer_name?: string;
    status: string;
    /** When the order was placed. */
    created_at: string;
    /** True seated / tab-open anchor for the turnover timer; may be absent. */
    table_opened_at?: string;
    total_amount: number;
    guest_count: number;
    /** True when the order is a seat-first open tab (POST /counter/table-tabs). */
    is_open_tab?: boolean;
    /** Non-voided line items on the order — 0 means seated but nothing ordered yet. */
    item_count?: number;
    server?: {
      id: string;
      first_name: string;
      last_name: string;
      role: string;
    };
  } | null;
}

export interface TableSession {
  /** 0 = not set yet */
  guestCount: number;
  /** Empty when no server assigned */
  serverId: string;
  serverDisplayName: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  guestBirthday?: string;
  notes?: string;
}

// Incoming online order (staging row) shown in the staff inbox. Mirrors the
// backend handlers.onlineOrderDTO (handlers/online_orders.go).
export interface OnlineOrderCartLine {
  product_id: string;
  name: string;
  quantity: number;
  unit_price: number;
  special_instructions?: string;
  /**
   * The customer's chosen options as names ("Large", "Honey BBQ"), snapshotted
   * at placement (F-MOD-N9). Display only — Accept prices from the option ids.
   */
  modifier_labels?: string[];
}

export interface OnlineOrder {
  id: string;
  status: 'placed' | 'accepted' | 'rejected' | 'expired';
  fulfillment_type: string;
  customer_name?: string | null;
  customer_phone?: string | null;
  customer_email?: string | null;
  delivery_address?: string | null;
  /**
   * The dine-in table a QR order came from (F-BE-N54). Null for pickup and
   * delivery. Show it wherever a dine-in online order is shown — without it a
   * "Dine-in" card names no table and nobody can tell whose order it is.
   */
  table_number?: string | null;
  notes?: string | null;
  /**
   * F-FE-N198: an order for later — when the customer wants it. Null ⇒ as soon
   * as possible. It is also the order's promise (F-ONL-N5) and it never
   * auto-cancels (F-ONL-N4).
   */
  requested_time?: string | null;
  cart: OnlineOrderCartLine[];
  subtotal: number;
  tax_amount: number;
  service_charge_amount: number;
  delivery_fee_amount: number;
  total_amount: number;
  payment_method: string;
  created_at: string;
  /** F-FE-N193: pending inbox only — the promo the edit sheet may drop, and how often staff edited it. */
  discount_amount?: number;
  promo_code?: string | null;
  edit_count?: number;
  // Delivery-zone snapshot (F-FE-N26) — present on zoned delivery orders.
  delivery_zone_name?: string | null;
  delivery_rider_charge?: number | null;
  delivery_eta_minutes?: number | null;
  delivery_lat?: number | null;
  delivery_lng?: number | null;
  // The named area the delivery was priced for. With no lat/lng it was picked
  // from the storefront's list because the map was unavailable (no pin).
  delivery_area?: string | null;
  /** F-ONL-N5 (inbox only): the "ready in" minutes Accept starts from. */
  suggested_minutes?: number;
  /** F-ONL-N4 (inbox only): when the unanswered-order sweeper will close it; absent when that is off. */
  expires_at?: string | null;
  /** F-ONL-N5: the time promised on Accept. Absent on orders accepted before promises existed. */
  promised_minutes?: number | null;
  promised_at?: string | null;
  // History-only fields (present on /counter/online-orders/history rows).
  rejected_reason?: string | null;
  accepted_at?: string | null;
  accepted_by_name?: string | null;
  order_id?: string | null;
  order_number?: string | null;
}

// F-FE-N193: removing items from a placed online order (after phoning the
// customer). Mirrors handlers/online_order_edit.go.
export interface OnlineOrderRemoval {
  /** Index into OnlineOrder.cart. */
  line: number;
  product_id: string;
  quantity: number;
}

export type OnlineOrderEditReason = 'out_of_stock' | 'customer_request' | 'other';

export interface OnlineOrderEditRequest {
  removals: OnlineOrderRemoval[];
  reason_code: OnlineOrderEditReason;
  note?: string;
  hide_product_ids?: string[];
  confirm_below_minimum?: boolean;
  dry_run?: boolean;
}

export interface OnlineOrderEditPreview {
  cart: OnlineOrderCartLine[];
  removed: { product_id: string; name: string; quantity: number; unit_price: number; modifier_labels?: string[] }[];
  subtotal: number;
  discount_amount: number;
  service_charge_amount: number;
  tax_amount: number;
  delivery_fee_amount: number;
  total_amount: number;
  total_before: number;
  promo_code_dropped: string | null;
  below_minimum: boolean;
  minimum_order: number;
  hidden_product_ids?: string[];
}

// An accepted online order whose real order is still live (not completed /
// cancelled), as served by /counter/online-orders/active for the dispatch
// board. order_status drives the board column; is_settled drives the
// Awaiting-settlement tray (COD money arrives after dispatch).
export interface ActiveOnlineOrder extends OnlineOrder {
  order_status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'out_for_delivery' | 'served' | string;
  order_total: number;
  paid_amount: number;
  is_settled: boolean;
  order_updated_at: string;
  /** Intent the order was priced at on accept — the settle modal's default tender. */
  pay_intent: 'cash' | 'card' | 'online' | string;
}

// F-ONL-N3: pause / busy for online ordering, as the board reads it.
// Mirrors handlers/online_ordering_status.go.
export interface OnlineOrderingStatus {
  mode: 'open' | 'busy' | 'paused';
  /** When a timed pause / busy spell ends; absent = until someone changes it. */
  until?: string | null;
  /** Busy only: minutes added to every quoted time. */
  extra_minutes?: number;
  set_by?: string;
  set_at?: string;
  /** F-ONL-N4: minutes before an unanswered order is closed; 0 = off. */
  unanswered_expire_minutes: number;
}

export interface SetOnlineOrderingStatusRequest {
  mode: 'open' | 'busy' | 'paused';
  /** Omit for "until I resume". */
  minutes?: number | null;
  extra_minutes?: number;
}

// F-ONL-N6: cancel an accepted online order from the board.
export interface CancelOnlineOrderRequest {
  pin: string;
  reason_code: string;
  note?: string;
  outcome: 'not_made' | 'wasted';
}

export interface CancelOnlineOrderResult {
  order_number: string;
  authorized_by: string;
  outcome: 'not_made' | 'wasted';
  waste_cost: number;
  void_kots: StationKOT[];
}

// Order Types
export interface Order {
  id: string;
  order_number: string;
  table_id?: string;
  user_id?: string;
  customer_id?: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  guest_birthday?: string;
  table_opened_at?: string;
  is_open_tab?: boolean;
  /** dine_in | takeout | delivery | custom ids from Settings → Order Types */
  order_type: string;
  /** Provenance of the order: 'pos' (counter/server) | 'online' (storefront). Used to badge online orders on the KDS board. */
  order_source?: string;
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'served' | 'completed' | 'cancelled';
  subtotal: number;
  tax_amount: number;
  discount_amount: number;
  /**
   * Discount percent (0–100) when the discount was entered as a percentage of
   * the subtotal; null/undefined when it was entered as a flat amount or
   * there is no discount. Receipts render "Discount (10%)" when this is set.
   */
  discount_percent?: number | null;
  /**
   * F-MKT-N2: the label the original receipt printed for the bill discount —
   * a bank card promo ("HBL Card Discount (20%)") or an item-only promo code
   * ("Promo DRINKS20 · Beverages"). Only GET /orders/:id sets it; absent =
   * the generic "Discount" label. Lets reprints match the original slip.
   */
  discount_label?: string | null;
  /**
   * Rolled-up sum of per-line discounts (`OrderItem.discount_amount`)
   * across non-voided lines. Distinct from `discount_amount`, which is
   * the bill-level manual discount. Receipts render the two on
   * separate rows; reports sum both.
   */
  item_discounts_total?: number;
  service_charge_amount?: number;
  /** Flat delivery fee for delivery orders; not part of F&B tax base. */
  delivery_fee_amount?: number;
  /**
   * FBR PKR 1 POS service fee actually charged on this order (F-MM-N9).
   * Persisted per order, never recomputed from current settings, so a
   * historical receipt reprints the fee it was issued with. Never part of the
   * tax base. 0 for opted-out and PRA tenants.
   */
  pos_fee_amount?: number;
  /**
   * F-MM-N23. Server-stamped at creation from the order type's config.
   * 'marketplace_settled' = tax 0 / POS fee 0, own number series, never filed
   * with FBR/PRA. Always read this — never re-derive from current settings.
   */
  tax_treatment?: 'standard' | 'marketplace_settled' | 'staff_meal';
  /** F-MM-N23. Memo estimate of the marketplace's commission (never a price input). */
  platform_commission_amount?: number;
  /** Per-order manual override of the delivery fee; null/undefined = settings default. */
  delivery_fee_override?: number | null;
  /**
   * Per-order service charge override (migration 081). At most one is set.
   * `_rate` is a fraction (0.05 == 5%) replacing the settings rate for this
   * order; `_amount` is a fixed figure replacing the percentage outright (the
   * receipt then prints no "(X%)"). Both null/undefined = settings rate.
   */
  service_charge_override_rate?: number | null;
  service_charge_override_amount?: number | null;
  /** F-CTRL-N80: what this bill would carry with no override; null on bills from before it existed. */
  service_charge_at_set_rate?: number | null;
  /** Destination for delivery orders (counter-entered or carried from the storefront). */
  delivery_address?: string | null;
  total_amount: number;
  checkout_payment_method?: 'cash' | 'card' | 'online';
  guest_count?: number;
  /** First KOT fire time — drives KDS timer */
  kot_first_sent_at?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  served_at?: string;
  completed_at?: string;
  /**
   * The local ticket an offline kitchen slip carried before this order had a
   * number, e.g. "OFF-A3F1-7" (F-CTRL-N52). Absent on every ordinary order.
   * Shown beside the real number so a slip still sitting in the pass can be
   * matched to the bill it became.
   */
  offline_ticket_ref?: string | null;
  /** True once a PRA tax invoice slip has been printed for this order. */
  pra_invoice_printed?: boolean;
  /** Invoice number printed on the PRA slip, if any; typically empty during rollout. */
  pra_invoice_number?: string | null;
  /** Timestamp (ISO) when the PRA slip was last printed. */
  pra_invoice_printed_at?: string;
  /** FBR/PRA government sync (IRN, QR) */
  fiscal_details?: FiscalDetails;
  /** Split-bill session (migration 055, F-CTRL-N14). */
  split_state?: OrderSplitState | null;
  table?: DiningTable;
  user?: User;
  items?: OrderItem[];
  payments?: Payment[];
}

/** One guest's entry in a split-bill session. Paid markers are written only
 *  by the backend payment path — never sent by the client. */
export interface SplitGuest {
  guest_no: number; // 1-based
  label: string;
  paid_payment_id?: string | null;
  paid_amount?: number | null;
  tender?: 'cash' | 'card' | 'online' | null;
  paid_at?: string | null;
}

/** Split-bill session persisted in orders.split_state (F-CTRL-N14). The
 *  order remains ONE order with ONE fiscal invoice — this only records how
 *  the total is presented and settled across guests. Mirrors backend
 *  models.SplitState. */
export interface OrderSplitState {
  version: number;
  mode: 'items' | 'even';
  guest_count: number;
  /** mixed: order billed at the standard cash rate (guests may mix tenders);
   *  card_only: order keeps the reduced card rate, cash tenders rejected. */
  tender_scope: 'mixed' | 'card_only';
  started_by: string;
  started_at: string;
  /** items mode: order_item id -> guest_no (string key) -> units. */
  assignments?: Record<string, Record<string, number>>;
  guests: SplitGuest[];
}

/** Client-writable subset sent to PUT /counter/orders/:id/split. */
export interface OrderSplitStateInput {
  mode: 'items' | 'even';
  guest_count: number;
  tender_scope: 'mixed' | 'card_only';
  assignments?: Record<string, Record<string, number>>;
}

export interface OrderItem {
  id: string;
  order_id: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  special_instructions?: string;
  /** >1 means item came from a subsequent KOT fire (delta) */
  kot_fire_generation?: number;
  kot_sent_at?: string;
  /** Station the line was routed to at fire time (KDS by-station / expo view). */
  station_id?: string | null;
  station_name?: string | null;
  /** Course this line belongs to (1=first). Drives coursing + KDS grouping. */
  course_number?: number;
  status: 'draft' | 'sent' | 'pending' | 'preparing' | 'ready' | 'served' | 'voided';
  /**
   * Resolved rupee discount on this line. Same semantics as the
   * order-level discount_amount but scoped to a single row.
   */
  discount_amount?: number;
  /**
   * 0–100 when the cashier entered a percent on this line; null/undefined
   * when entered as a flat amount or no discount. Receipts render
   * "Discount (50%)" when set.
   */
  discount_percent?: number | null;
  created_at: string;
  updated_at: string;
  product?: Product;
  notes?: string; // Alternative field name for special instructions
  /**
   * Chosen options as SOLD, with snapshotted names (F-MOD-N6). Present on
   * order reads — the rail, the payment panel and the printed bill.
   */
  modifiers?: OrderItemModifier[];
  /**
   * Pre-formatted option labels ("Extra Shot x2"). The KDS board's payload
   * carries these instead of the full rows, because the card only ever
   * displays them and the board refetches on every SSE tick.
   */
  modifier_labels?: string[];
  /** The product's price before modifiers were folded in. Display/audit only. */
  base_unit_price?: number;
  /** F-DEAL-N1: a deal line's fixed parts as printed ("Zinger Burger", "Fries x2"). */
  combo_parts?: string[];
  /** F-DEAL-N2: the price rule that set this line's price ("Happy hour") and the menu price it replaced. */
  price_rule_label?: string;
  list_unit_price?: number;
}

/**
 * Counter-side shape of the per-order service charge override. `rate` is a
 * fraction (0.05 == 5%) — the bill still scales and the receipt prints
 * "(5%)". `amount` is a fixed figure — the receipt prints no percentage.
 * Mirrors orders.service_charge_override_rate / _amount (migration 081).
 */
export type ServiceChargeOverride =
  | { kind: 'rate'; value: number }
  | { kind: 'amount'; value: number };

/** Read the override carried on an Order (amount wins if both present). */
export function serviceChargeOverrideFromOrder(
  o: Pick<Order, 'service_charge_override_rate' | 'service_charge_override_amount'> | null | undefined
): ServiceChargeOverride | null {
  if (!o) return null;
  if (o.service_charge_override_amount != null) {
    return { kind: 'amount', value: o.service_charge_override_amount };
  }
  if (o.service_charge_override_rate != null) {
    return { kind: 'rate', value: o.service_charge_override_rate };
  }
  return null;
}

export interface CreateOrderRequest {
  table_id?: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  guest_birthday?: string;
  /** dine_in | takeout | delivery | custom ids from Settings → Order Types */
  order_type: string;
  guest_count?: number;
  items: CreateOrderItem[];
  notes?: string;
  /** Destination address; required for delivery orders. */
  delivery_address?: string;
  /** Per-order delivery fee override set at creation (delivery orders only). */
  delivery_fee_override?: number;
  /** Per-order service charge override set at creation — send at most one. */
  service_charge_override_rate?: number;
  service_charge_override_amount?: number;
  /** F-CTRL-N80: reason / manager PIN when a service charge waived at creation needs them. */
  service_charge_reason_code?: string;
  service_charge_reason_note?: string;
  service_charge_pin?: string;
  /** Assigned server for dine-in (counter flow); maps to order.user_id on server */
  assigned_server_id?: string;
  /**
   * Idempotency key for ONE cart submission (F-FE-N103).
   *
   * Must be minted per SUBMISSION, not per call: two calls racing to send the
   * same cart have to carry the SAME key, or the server sees two distinct
   * submissions and duplicates exactly as before. Reused unchanged on a retry
   * after a timeout, which is what makes re-sending an ambiguous request safe.
   */
  client_op_id?: string;
}

/** Counter: open dine-in tab (order number + empty bill) after table session modal. */
export interface OpenCounterTableTabRequest {
  table_id: string;
  /** Omit or 0 if unknown — editable on the rail until checkout closes */
  guest_count?: number;
  /** Omit if no server yet — editable on the rail until checkout closes */
  assigned_server_id?: string;
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  guest_birthday?: string;
  notes?: string;
}

/** Counter: party size + assigned server on an open dine-in order */
export interface UpdateCounterOrderServiceRequest {
  guest_count: number;
  /** Empty string clears assigned server */
  assigned_server_id: string;
  /** Omit to leave unchanged; empty string clears notes */
  notes?: string;
}

/**
 * Product modifiers / variants (F-MOD-N4).
 *
 * Shapes mirror backend/internal/models/modifiers.go. Configuration only —
 * the chosen-option wire type is CreateOrderItemModifier below.
 */
export type ModifierPriceModeT = 'add' | 'replace';
export type ModifierSelectionType = 'single' | 'multi';

/**
 * D5: choosing this option retunes ANOTHER group's required count — picking
 * "Large" makes Flavours demand 3 scoops where "Medium" demands 2. Data, not
 * code, so a parlour changes its scoop counts without a deploy.
 */
export interface ModifierOptionGroupRule {
  target_group_id: string;
  min_select?: number | null;
  max_select?: number | null;
}

export interface ModifierOption {
  id: string;
  group_id: string;
  name: string;
  /**
   * Interpreted by the GROUP's price mode: for 'add' it is added to the line's
   * unit price (0 being a free choice), for 'replace' it IS the line's new base
   * price. Never negative — a negative option would be a discount with no
   * manager PIN and no void_log entry (invariant #11).
   */
  price_delta: number;
  cost_delta?: number | null;
  linked_product_id?: string | null;
  /** The 86 switch. Sold-out options still render, struck through. */
  is_available: boolean;
  is_default: boolean;
  sort_order: number;
  group_rules?: ModifierOptionGroupRule[];
  /** F-MOD-N11: scales the parent's base recipe when chosen (Whole Cake = 8). 1 = none. */
  recipe_multiplier?: number;
  /** True when the option carries recipe rows or a multiplier other than 1 —
   *  it is then costed from that recipe and cost_delta is ignored. */
  has_recipe?: boolean;
}

export interface ModifierGroup {
  id: string;
  name: string;
  /** What the cashier reads in the configurator ("Choose a size"). */
  prompt: string;
  selection_type: ModifierSelectionType;
  price_mode: ModifierPriceModeT;
  /** Counted in UNITS (sum of selection quantities), not distinct options. */
  min_select: number;
  /** null = unlimited. */
  max_select: number | null;
  allow_quantity: boolean;
  max_per_option: number;
  is_active: boolean;
  sort_order: number;
  created_at?: string;
  updated_at?: string;
  options?: ModifierOption[];
}

/** A group as it applies to one product, with the attachment override folded in. */
export interface ResolvedModifierGroup extends ModifierGroup {
  /** Which attachment won: a direct one on the item, or the category's. */
  source: 'product' | 'category';
}

/**
 * The whole menu's modifier configuration in one payload. One request, so it
 * is one cacheable query key — which is what lets the configurator survive an
 * outage. Product group ids arrive ALREADY RESOLVED; never re-derive the
 * inheritance rule client-side.
 */
export interface ProductModifierConfig {
  groups: ModifierGroup[];
  product_groups: Record<string, string[]>;
  /**
   * F-MOD-N13: productId → optionId → that product's override of a shared
   * option. Sparse. Absent on a backend older than migration 176.
   */
  option_overrides?: Record<string, Record<string, ModifierOptionOverride>>;
}

/** One product's override of one shared option (F-MOD-N13). Unset = the option's own value. */
export interface ModifierOptionOverride {
  price_delta?: number;
  is_available?: boolean;
}

/** Every attachment in the tenant, as the menu matrix reads it (F-MOD-N12). */
export interface ModifierAttachments {
  /** category id → group ids, in the order the cashier is asked. */
  categories: Record<string, string[]>;
  /** product id → direct attachments and category opt-outs. */
  products: Record<string, { direct: string[]; excluded: string[] }>;
}

/** One chosen option as sold, returned on order reads. Names are snapshots. */
export interface OrderItemModifier {
  id: string;
  group_id?: string | null;
  option_id?: string | null;
  group_name: string;
  option_name: string;
  price_mode: ModifierPriceModeT;
  unit_price_delta: number;
  /** PER PARENT UNIT: total sold units are this times the line quantity. */
  quantity: number;
  sort_order: number;
}

/** One product's reachable price once a group is attached to its category. */
export interface ModifierPriceImpactRow {
  product_id: string;
  product_name: string;
  current_price: number;
  min_price_after: number;
  /** null when the group is unbounded — the customer can keep adding. */
  max_price_after: number | null;
}

/**
 * The 409 body returned when attaching a group to a CATEGORY would change what
 * customers are charged. The write did not happen; re-send with
 * confirm_price_impact once the owner has seen this.
 */
export interface ModifierCategoryPriceImpact {
  [groupId: string]: {
    group_name: string;
    price_mode: ModifierPriceModeT;
    requires_choice: boolean;
    option_count: number;
    products: ModifierPriceImpactRow[];
  };
}

/**
 * One chosen modifier option on an order line (F-MOD-N1).
 *
 * An option id and a count, and DELIBERATELY NO PRICE. The server reads
 * modifier_options.price_delta inside the order's own transaction, exactly as
 * it already reads products.price. Adding a price field here would breach
 * CLAUDE.md invariant #11 — a contract test pins its absence.
 *
 * `quantity` is PER PARENT UNIT: two lattes each with one extra shot is a line
 * of quantity 2 carrying a modifier of quantity 1.
 */
export interface CreateOrderItemModifier {
  option_id: string;
  /** Omit or 0 ⇒ 1. */
  quantity?: number;
}

export interface CreateOrderItem {
  product_id: string;
  quantity: number;
  special_instructions?: string;
  /** Course this line belongs to (1=first). Omit ⇒ course 1. */
  course_number?: number;
  /** Chosen modifier options. Omit when the line has none. */
  modifiers?: CreateOrderItemModifier[];
}

export interface UpdateOrderStatusRequest {
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'out_for_delivery' | 'served' | 'completed' | 'cancelled';
  notes?: string;
}

// Payment Types
/**
 * Unified payment vocabulary across the entire app:
 *   cash    — physical cash
 *   card    — debit + credit card terminal
 *   online  — bank transfer / digital wallets / payment links
 *
 * Legacy values (`credit_card`, `debit_card`, `digital_wallet`) are
 * kept on the type union for backwards-compatibility while reading
 * historical orders/payments — the UI must never SEND them.
 */
export type PaymentMethod =
  | 'cash'
  | 'card'
  | 'online'
  /**
   * F-MM-N24: a bill leg settled from a gift card balance. It is a way to
   * PAY, never a tax intent — the order's rate still comes from
   * checkout_payment_method, which stays cash|card|online. Anything that
   * chooses a tax rate must key off TenderIntent, not this union.
   */
  | 'gift_card'
  /** F-MM-N30 — the bill went on a staff meal allowance or a customer tab. */
  | 'house_account'
  | 'credit_card'
  | 'debit_card'
  | 'digital_wallet';

export interface Payment {
  id: string;
  order_id: string;
  payment_method: PaymentMethod;
  /** Online-provider attribution slug (easypaisa / jazzcash / …), migration 050.
   *  Only set for online tenders when the tenant has sub-methods enabled. */
  payment_sub_method?: string;
  amount: number;
  reference_number?: string;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  processed_by?: string;
  processed_at?: string;
  created_at: string;
  processed_by_user?: User;
}

export interface ProcessPaymentRequest {
  payment_method: 'cash' | 'card' | 'online' | 'gift_card';
  /** Online-provider slug. Only valid when payment_method === 'online' and the
   *  tenant has configured active sub-methods; the counter requires a pick. */
  payment_sub_method?: string;
  amount: number;
  reference_number?: string;
  /** F-MM-N4: declares the intent whose tax rate the ORDER is billed at,
   *  independent of this tender's method. The split flow sends 'cash' for
   *  mixed splits so a first-tender card doesn't lock the reduced rate. */
  rate_intent?: 'cash' | 'card' | 'online';
  /** F-CTRL-N14: which split guest (1-based) this tender settles. */
  split_guest_no?: number;
  /** F-MM-N24: which gift card to draw down. Required when payment_method is
   *  'gift_card' and rejected otherwise. Sent with or without the GC- prefix. */
  gift_card_code?: string;
}

/** F-MM-N24: what the server returns for a gift card leg — the payment row
 *  plus what is LEFT on the card. The balance is computed from the card's
 *  append-only ledger server-side; the till prints this number rather than
 *  subtracting locally, so a stale cached balance can never reach a receipt. */
export interface GiftCardPaymentResult {
  payment: Payment;
  gift_card_code: string;
  gift_card_balance: number;
  gift_card_spent: boolean;
}

// ── House accounts (F-MM-N30, migration 154) ─────────────────────────────

export type HouseAccountKind = 'staff_allowance' | 'tab';
export type HouseAccountStatus = 'active' | 'suspended' | 'closed';
/** The tax rate a tab bill is priced at. Card-rate bills settle by card/online only. */
export type TabRateIntent = 'cash' | 'card';

export interface HouseAccount {
  id: string;
  /** F-MM-N31: null on a staff allowance owned by a login or a no-login
   *  person. Always set on a tab. */
  customer_id: string | null;
  /** The person (same as person_name), kept for existing screens. */
  customer_name: string;
  customer_phone?: string;
  /** Login's full name, else the no-login holder, else the legacy customer. */
  person_name: string;
  /** A person without POS access (staff allowance only). */
  holder_name?: string;
  holder_phone?: string;
  /** True when the allowance belongs to a staff login. */
  has_login: boolean;
  /** Whether that login is active (only when has_login). */
  login_active?: boolean;
  kind: HouseAccountKind;
  status: HouseAccountStatus;
  user_id?: string;
  user_name?: string;
  monthly_allowance: number;
  credit_limit: number;
  tab_rate_intent: TabRateIntent;
  note?: string;
  created_at: string;
  updated_at: string;
  /** The ledger sum. Tab: what the guest owes now. Staff: all-time
   *  consumption (every meal charged, net of undos) — not money owed. */
  balance: number;
  /** YYYY-MM — the current business month the allowance figures cover. */
  cycle_month: string;
  allowance_used: number;
  allowance_remaining: number;
  overage_this_month: number;
  /** Tab with a credit limit only. */
  credit_available?: number;
}

export interface TabOpenBill {
  order_id: string;
  order_number: string;
  rate_intent: TabRateIntent;
  charged_at: string;
  charged: number;
  outstanding: number;
}

export interface CounterHouseAccountDetail {
  account: HouseAccount;
  open_bills: TabOpenBill[];
  /** Portion of the balance cash may settle (cash-rate bills only). */
  cash_payable: number;
}

export interface ChargeToAccountResult {
  payment_id: string;
  order_id: string;
  order_number: string;
  account_id: string;
  account_kind: HouseAccountKind;
  customer_name: string;
  amount: number;
  tax_treatment: 'standard' | 'staff_meal';
  approved_by?: string;
  over_limit: boolean;
  allowance_covered?: number;
  overage?: number;
  allowance_remaining?: number;
  cycle_month?: string;
  balance?: number;
  rate_intent?: TabRateIntent;
}

export interface TabSettlementResult {
  settlement_id: string;
  account_id: string;
  customer_name: string;
  method: 'cash' | 'card' | 'online';
  amount: number;
  business_date: string;
  balance_after: number;
  /** Bills this payment cleared, oldest first. */
  allocations: { order_id: string; order_number: string; amount: number }[];
}

/** POST /counter/orders/:id/undo-staff-meal (F-MM-N30 hotfix). */
export interface UndoStaffMealRequest {
  manager_pin: string;
  reason: string;
  /** true ⇒ stock stays deducted and is logged as waste; false ⇒ stock goes back. */
  food_made: boolean;
  /** A `wasted` void reason code; only meaningful with food_made. */
  reason_code?: string;
}

export interface UndoStaffMealResult {
  order_id: string;
  order_number: string;
  account_id: string;
  customer_name: string;
  amount: number;
  cycle_month: string;
  allowance_remaining: number;
  food_made: boolean;
}

export interface HouseAccountStatementEntry {
  id: string;
  entry_type: 'charge' | 'charge_reversal' | 'settlement' | 'adjustment';
  amount: number;
  order_id?: string;
  order_number?: string;
  business_date?: string;
  cycle_month?: string;
  allowance_covered: number;
  overage: number;
  rate_intent?: TabRateIntent;
  over_limit_override: boolean;
  approved_by?: string;
  created_by?: string;
  method?: string;
  note?: string;
  created_at: string;
  running_balance: number;
  menu_value?: number;
}

export interface HouseAccountStatement {
  account: HouseAccount;
  from: string;
  to: string;
  opening_balance: number;
  closing_balance: number;
  entries: HouseAccountStatementEntry[];
}

export interface StaffMonthRow {
  account_id: string;
  /** The person (same as person_name), kept for existing screens. */
  customer_name: string;
  /** F-MM-N31: login's full name, else the no-login holder, else the legacy customer. */
  person_name?: string;
  user_id?: string;
  /** False for a person without POS access. */
  has_login?: boolean;
  user_name?: string;
  monthly_allowance: number;
  meals: number;
  consumed: number;
  allowance_used: number;
  overage: number;
  cost: number;
  overage_entries: {
    order_id: string;
    order_number: string;
    business_date: string;
    charged_at: string;
    amount: number;
    overage: number;
    approved_by?: string;
  }[];
}

export interface TabAgingRow {
  account_id: string;
  customer_name: string;
  customer_phone?: string;
  status: HouseAccountStatus;
  credit_limit: number;
  balance: number;
  days_0_7: number;
  days_8_30: number;
  days_31_plus: number;
  oldest_bill_at?: string;
}

/** One configured online payment sub-method (Easypaisa/JazzCash/…), migration 050. */
export interface OnlineSubMethod {
  slug: string;
  label: string;
  is_active: boolean;
  sort_order: number;
}

/** The admin config blob at app_settings.payment.online_submethods. */
export interface OnlineSubMethodConfig {
  enabled: boolean;
  methods: OnlineSubMethod[];
}

/** Matches backend pricing.Settings (fractions 0–1) */
export interface PricingSettings {
  tax_rate_cash: number;
  tax_rate_card: number;
  tax_rate_online: number;
  service_charge_rate: number;
  // P0 (2026-05) — tax correctness. See docs/TAX_FISCAL_P0_PLAN.md.
  tenant_jurisdiction?: string; // "" | "punjab" | "ict"
  pra_rims_live?: boolean;
  delivery_excluded_from_tax_base?: boolean;
  service_charge_excluded_from_tax_base?: boolean;
  /** Service charge computed on the gross subtotal instead of post-discount. Per tenant; default false. */
  service_charge_on_gross_subtotal?: boolean;
  /**
   * FBR POS service fee in rupees (F-MM-N9). ALREADY resolved server-side
   * against the enable flag and the tenant's fiscal authority, so it is 0 for
   * PRA and opted-out tenants. The client applies no gate of its own.
   *
   * Never enters the tax base — see computeCartTotals.
   */
  pos_service_fee?: number;
  /** Active online sub-methods for the counter tender pad (migration 050).
   *  Empty/absent when the feature is off — counter uses the plain online flow. */
  online_submethods?: OnlineSubMethod[];
}

export interface CounterServer {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
}

export interface PaymentSummary {
  order_id: string;
  total_amount: number;
  total_paid: number;
  pending_amount: number;
  remaining_amount: number;
  is_fully_paid: boolean;
  payment_count: number;
}

// Cart Types (Frontend Only)
export interface CartItem {
  product: Product;
  quantity: number;
  special_instructions?: string;
}

export interface Cart {
  items: CartItem[];
  subtotal: number;
  tax_amount: number;
  total_amount: number;
}

// Dashboard Types (legacy /admin/dashboard/stats — kept for backward compat)
export interface DashboardStats {
  today_orders: number;
  /** Total orders placed today, any status. Filled by the new backend. */
  today_orders_placed?: number;
  today_revenue: number;
  active_orders: number;
  occupied_tables: number;
}

// ───────────────────────────────────────────────────────────────────────────
// Dashboard v2 — typed payloads mirroring backend/internal/models/dashboard.go.
// All money is currency-major units. All *_label fields are pre-formatted
// DD-MM-YYYY (or HH:mm for sub-day buckets) — never reformat on the client.
// ───────────────────────────────────────────────────────────────────────────

export type DashboardPeriod =
  | 'today'
  | 'yesterday'
  | '7d'
  | '30d'
  | 'cw'
  | 'cm'
  | 'custom';

export interface DashboardOverview {
  period: DashboardPeriod;
  from: string;
  to: string;
  from_label: string;
  to_label: string;
  previous_from: string;
  previous_to: string;
  previous_from_label: string;
  previous_to_label: string;
  timezone: string;
  net_sales: MetricPair;
  gross_sales: MetricPair;
  tax: MetricPair;
  discounts: MetricPair;
  /** Service charge collected — distributed to staff, not revenue; excludes
   *  orders fully reversed by credit note. Optional for pre-F-RPT-N17
   *  backends. */
  service_charge?: MetricPair;
  /** Completed orders only — matches the revenue denominator. */
  orders: IntMetricPair;
  /** All orders placed in the window (any status). Useful drop-off signal. */
  orders_placed: IntMetricPair;
  covers: IntMetricPair;
  avg_ticket: MetricPair;
  /** Refunds issued + gross after refunds (omitted on older API responses). */
  refunds_issued?: MetricPair;
  gross_sales_after_refunds?: MetricPair;
  /** Output VAT after FBR credit-note reversals + the reversed magnitude itself. */
  credit_note_tax?: MetricPair;
  net_sales_tax?: MetricPair;
  expenses: MetricPair;
  net_profit: MetricPair;
  /**
   * NetProfit − RefundsIssued. Operator's real end-of-day economic
   * position on days with credit notes — the food was still produced
   * (so COGS is real) but the cash went back out the till. Surface
   * this prominently when refunds are non-zero; fall back to
   * `net_profit` when zero (the two are equal in that case).
   * Optional for backward compat with the pre-F-RPT-N1 API surface.
   */
  net_profit_after_refunds?: MetricPair;
  /**
   * Projected gross margin (companion to expense-ledger Net Profit).
   * Derived from `products.projected_cost` — kept SEPARATE from
   * `expenses` / `net_profit` so the two profit numbers can be
   * reconciled without double-counting.
   */
  projected_cogs?: MetricPair;
  projected_margin?: MetricPair;
  projected_margin_pct?: MetricPair;
  /** % of net sales attributable to items that have a projected cost set (0-100). */
  projected_coverage_pct?: MetricPair;
  /** F-RPT-N41: cost-of-goods basis of the current window. Absent = at_sale. */
  cogs_basis?: CogsBasisWindow;
}

export interface LivePulse {
  active_orders: number;
  in_kitchen: number;
  ready_to_serve: number;
  /** Orders in active states but past the kitchen.stale_minutes window
   *  — hidden from the KDS, surfaced separately so they don't pollute the
   *  live ops gauges or kitchen-wait stats. */
  stale_orders_count: number;
  stale_threshold_minutes: number;
  occupied_tables: number;
  total_tables: number;
  avg_kitchen_wait_seconds: number;
  longest_running_seconds: number;
  voids_today_count: number;
  voids_today_amount: number;
  orders_today_count: number;
  revenue_today_so_far: number;
  drawer_reconciled: boolean;
  drawer_expected_cash: number;
  generated_at: string;
}

export interface SalesBucket {
  bucket_start: string;
  /** Server-formatted: "14:00" for hour, "18-04" for day, "Apr 26" for month. */
  label: string;
  orders: number;
  gross: number;
  tax: number;
  net: number;
}

export interface SalesTimeseries {
  period: DashboardPeriod;
  granularity: 'hour' | 'day' | 'month';
  from: string;
  to: string;
  current: SalesBucket[];
  prior: SalesBucket[];
}

export interface DashboardTopItem {
  product_id: string;
  name: string;
  category?: string | null;
  qty_sold: number;
  revenue: number;
  percent_of_net: number;
}

export interface PaymentMixSlice {
  method: string;
  label: string;
  count: number;
  amount: number;
  pct: number;
}

export interface OrderTypeMixSlice {
  order_type: string;
  label: string;
  count: number;
  amount: number;
  pct: number;
}

export type DashboardAlertSeverity = 'info' | 'warning' | 'critical';
export type DashboardAlertKind =
  | 'low_stock'
  | 'expiring_stock'
  | 'void_spike'
  | 'void_waste'
  | 'long_order'
  | 'stale_orders'
  | 'drawer_unreconciled'
  | 'no_sales'
  | 'guest_feedback_low';

export interface DashboardAlert {
  id: string;
  severity: DashboardAlertSeverity;
  kind: DashboardAlertKind;
  title: string;
  detail: string;
  /** Admin section id to navigate to when the user clicks the alert. */
  action_to?: string;
}

/**
 * One row of the dashboard activity feed (F-RPT-N44). Built server-side from
 * the current business day's sales, voids, cancellations and refunds, so the
 * feed survives a refresh and reads the same on every device.
 */
export type DashboardActivityKind = 'sale' | 'void' | 'cancel' | 'refund';

export interface DashboardActivityEntry {
  /** Stable per source row: sale:<order>, void:<void_log>, cancel:<order>, refund:<note>. */
  id: string;
  kind: DashboardActivityKind;
  title: string;
  detail: string;
  /** Sale / void / cancel value, or the refund as a negative amount. */
  amount?: number;
  order_id?: string;
  order_number?: string;
  /** RFC3339 UTC. */
  at: string;
}

export interface DashboardActivityPage {
  /** The business day shown; absent when no day has been opened yet. */
  business_date?: string;
  entries: DashboardActivityEntry[];
  has_more: boolean;
}

export interface SalesReportItem {
  date: string;
  order_count: number;
  revenue: number;
}

export interface OrdersReportItem {
  status: string;
  count: number;
  avg_amount: number;
}

// ───────────────────────────────────────────────────────────────────────────
// Reports v2 — payload types mirroring backend/internal/models/models.go.
// ───────────────────────────────────────────────────────────────────────────

export interface MetricPair {
  current: number;
  previous: number;
  delta: number;
  /** null when previous is 0 — the UI should render "—" rather than "+∞%". */
  pct?: number | null;
}

export interface IntMetricPair {
  current: number;
  previous: number;
  delta: number;
  pct?: number | null;
}

export interface TenderMixRow {
  method: string;
  amount: number;
  count: number;
  pct: number;
}

/** Breakdown of the "online" tender slice by configured provider (migration 050).
 *  Empty when the feature is off / nothing attributed. Amounts sum to the online
 *  TenderMixRow.amount; pct is the share of the online total. */
export interface OnlineSubMethodMixRow {
  slug: string;
  label: string;
  amount: number;
  count: number;
  pct: number;
}

export interface OverviewReport {
  from: string;
  to: string;
  from_label: string;
  to_label: string;
  previous_from: string;
  previous_to: string;
  previous_from_label: string;
  previous_to_label: string;
  timezone: string;
  gross_sales: MetricPair;
  discounts: MetricPair;
  net_sales: MetricPair;
  tax: MetricPair;
  service_charge: MetricPair;
  orders: IntMetricPair;
  covers: IntMetricPair;
  average_check: MetricPair;
  /** Present on API v2 overview after refunds KPI rollout. */
  refunds_issued?: MetricPair;
  gross_sales_after_refunds?: MetricPair;
  tender_mix: TenderMixRow[];
  /** Online tender broken down by configured sub-method (migration 050).
   *  Empty when the feature is off or nothing was attributed. */
  tender_mix_online_submethods?: OnlineSubMethodMixRow[];
  /** Sales segmented by order type, net of refunds (sums to
   *  gross_sales_after_refunds). Empty when no completed orders in window.
   *  Resolve custom labels/colors from enabled_order_types on the frontend. */
  order_type_mix?: OrderTypeMixSlice[];
  /**
   * Projected gross margin metrics — derived from products.projected_cost.
   * Excludes lines whose product has no cost set; `projected_coverage_pct`
   * (0-100) tells the UI how much of net sales the metric covers so the
   * user can judge how trustworthy it is.
   */
  projected_cogs?: MetricPair;
  projected_margin?: MetricPair;
  projected_margin_pct?: MetricPair;
  projected_coverage_pct?: MetricPair;
  /** F-RPT-N41: cost-of-goods basis of the current window. Absent = at_sale. */
  cogs_basis?: CogsBasisWindow;
  operating_expenses?: MetricPair;
  net_profit?: MetricPair;
}

// Reports → Discounts & service charge (F-RPT-N51 / F-FE-N212).
// Discounts = SUM(discount_amount + item_discounts_total), as Daily Sales and the
// Overview KPI; the five parts always add back to it. Service charge leaves bills
// fully cancelled by credit note out, as the Overview KPI does, and is always
// configured + custom (a waived bill carries none).
export interface AdjustmentsDayRow {
  date: string;
  date_label: string;
  orders: number;
  subtotal: number;
  service_charge: number;
  sc_configured_orders: number;
  sc_configured_amount: number;
  sc_custom_orders: number;
  sc_custom_amount: number;
  sc_waived_orders: number;
  discounted_orders: number;
  discounts: number;
  discount_manual_bill: number;
  discount_manual_lines: number;
  discount_promo: number;
  discount_card_promo: number;
  discount_loyalty: number;
  /** F-RPT-N52: below / above what the bills would carry at the set rate. */
  sc_given_up: number;
  sc_added: number;
  /** Custom or waived bills from before that figure was stored — counted, never estimated. */
  sc_unknown_orders: number;
}

/** F-RPT-N52: one reason ("" = none recorded) behind by-hand discounts and service charge given up. */
export interface AdjustmentsReasonRow {
  code: string;
  discounts: number;
  discounted_orders: number;
  sc_given_up: number;
  sc_waived_orders: number;
}

export interface AdjustmentsStaffRow {
  user_id: string | null;
  name: string;
  /** Bills this person rang; discounts are theirs. */
  orders: number;
  discounted_orders: number;
  discounts: number;
  discount_manual_bill: number;
  discount_manual_lines: number;
  discount_promo: number;
  discount_card_promo: number;
  discount_loyalty: number;
  /** Service-charge overrides this person SET (may be on someone else's bill). */
  sc_custom_set: number;
  sc_waived_set: number;
  /** F-RPT-N52: changes this person approved with their manager PIN. */
  approvals?: number;
}

export interface AdjustmentsReport {
  /** The rate checkout charges when nobody overrides it (0.1 = 10%). */
  service_charge_rate: number;
  days: AdjustmentsDayRow[];
  staff: AdjustmentsStaffRow[];
  /** F-RPT-N52: by-hand discounts only — offers carry their own source. */
  reasons?: AdjustmentsReasonRow[];
}

export type AdjustmentScKind = 'configured' | 'custom' | 'waived' | 'none' | 'credit_noted';

export interface AdjustmentsOrderRow {
  id: string;
  order_number: string;
  completed_at: string;
  cashier: string;
  subtotal: number;
  total: number;
  service_charge: number;
  sc_kind: AdjustmentScKind;
  sc_override_rate: number | null;
  sc_override_amount: number | null;
  sc_override_by: string;
  discounts: number;
  discount_manual_bill: number;
  discount_manual_lines: number;
  discount_promo: number;
  discount_card_promo: number;
  discount_loyalty: number;
  credit_noted: boolean;
  /** F-RPT-N52: the latest logged reasons and approver; "" when none. */
  discount_reason?: string;
  discount_reason_note?: string;
  sc_reason?: string;
  sc_reason_note?: string;
  approved_by?: string;
  /** Rupees below the set rate on this bill; null when unknown or none. */
  sc_given_up?: number | null;
}

export interface DailySalesRow {
  date: string;
  date_label: string;
  orders: number;
  covers: number;
  /** SUM(subtotal): pre-discount, pre-tax line-item total. Useful for discount-intensity (discounts / subtotal). */
  subtotal: number;
  /** SUM(total_amount): post-discount, tax-inclusive — what the customer paid. Matches the Admin Dashboard "Gross sales" KPI. */
  gross: number;
  /** SUM(discount_amount): already reflected inside `gross`. */
  discounts: number;
  /** SUM(total_amount - tax_amount) = gross − tax. Revenue we actually earn (tax is a liability, not revenue). */
  net: number;
  /** SUM(tax_amount): FBR/PRA liability included inside `gross`. */
  tax: number;
  /** Service charge, bills fully refunded by credit note left out (F-RPT-N51). Inside `gross`. */
  service_charge?: number;
}

export interface HourlySeriesPoint {
  hour_start: string;
  hour_start_label: string;
  orders: number;
  net: number;
}

export interface HourlyHeatmapCell {
  dow: number;   // 0=Sunday … 6=Saturday
  hour: number;  // 0..23
  orders: number;
  net: number;
}

export interface HourlySalesReport {
  series: HourlySeriesPoint[];
  heatmap: HourlyHeatmapCell[];
}

export interface ItemSalesRow {
  product_id: string;
  name: string;
  category?: string | null;
  qty_sold: number;
  gross: number;
  net: number;
  orders_count: number;
  percent_of_net: number;
  avg_unit_price: number;
  /**
   * Projected margin fields — populated only when the product has a
   * projected_cost set. UI flags rows missing a cost so the operator can
   * spot coverage gaps.
   */
  projected_cost?: number | null;
  projected_cogs?: number | null;
  projected_margin?: number | null;
  projected_margin_pct?: number | null;
  /**
   * Group-mode fields (present only when group_by is category or tag). key_id
   * uniquely identifies the row's group regardless of grouping; item_count is
   * the distinct products in the group; share_of_category is a tag row's net as
   * a % of its category's full net (the "Summer Special = 18% of Coffee" figure).
   */
  /**
   * Per-channel split of qty_sold, keyed by order_type id (dine_in / takeout /
   * delivery / tenant-defined ids, plus 'other' for legacy blanks). Buckets are
   * mutually exclusive and always sum to qty_sold. Only channels with volume in
   * the window appear, so a dine-in-only tenant gets no delivery column.
   * Present in item and category modes only.
   */
  qty_by_channel?: Record<string, number>;
  /**
   * Units that arrived via the storefront (order_source='online'). A SUBSET of
   * qty_by_channel, not a peer — an online delivery counts under 'delivery' AND
   * here. Absent when the tenant sold nothing online in the window.
   */
  qty_online?: number;
  key_id?: string;
  item_count?: number;
  share_of_category?: number | null;
}

export interface TableSalesRow {
  table_id?: string | null;
  table_number: string;
  location?: string | null;
  zone?: string | null;
  seating_capacity?: number | null;
  parties: number;
  covers: number;
  net_sales: number;
  avg_check: number;
  avg_covers_per_party: number;
  revenue_per_cover: number;
}

export interface PartySizeRow {
  bucket: string;
  min_size: number;
  /** 0 means "no upper bound" (e.g. 9+ guests). */
  max_size: number;
  parties: number;
  covers: number;
  net_sales: number;
  avg_check: number;
  revenue_per_cover: number;
}

export interface OrdersBrowserRow {
  id: string;
  order_number: string;
  table_number?: string | null;
  server_name?: string | null;
  customer_name?: string | null;
  guest_count: number;
  total_amount: number;
  checkout_payment_method?: 'cash' | 'card' | 'online' | string | null;
  /** F-RPT-N30 — business day the order belongs to (range lists span days). */
  business_date: string; // YYYY-MM-DD
  business_date_label: string; // DD-MM-YYYY
  /** Online provider slug of the largest payment leg (migration 050), + its label. */
  payment_sub_method?: string | null;
  payment_sub_method_label?: string | null;
  /** True when the bill was settled by more than one tender. */
  split_tender: boolean;
  /** F-MM-N30 — charged to a house account (tab or staff meal), not paid at the counter. */
  charged_to_account?: boolean;
  /** Raw order-type id: dine_in / takeout / delivery, or a custom ot_* id.
   *  Resolve to a display label with displayLabelForOrderType(). */
  order_type?: string;
  status: string;
  created_at: string;
  created_at_label: string;
  completed_at?: string | null;
  completed_at_label?: string | null;
  pra_invoice_printed: boolean;
  pra_invoice_number?: string | null;
  pra_invoice_printed_at?: string | null;
  pra_invoice_printed_at_label?: string | null;
  pra_invoice_reprint_count: number;
  pra_invoice_last_reprinted_at?: string | null;
  pra_invoice_last_reprinted_by_name?: string | null;
  pra_late_window_expires_at?: string | null;
  pra_late_window_seconds_remaining?: number | null;
  can_print_pra: boolean;
  can_print_pra_reason?: string | null;
  /** SYNCED | PENDING | FAILED | EXEMPT (F-MM-N23 marketplace-settled, never filed) | null. */
  fiscal_status?: string | null;
  /** F-MM-N23. 'marketplace_settled' rows are never filed and carry no PRA slip. */
  tax_treatment?: string;
  fiscal_authority?: string | null;
  fiscal_irn?: string | null;
  // Credit-note linkage. When `cancelled_by_credit_note` is true the original
  // invoice should be greyed out and rendered alongside the offsetting
  // credit note for net-zero audit math.
  cancelled_by_credit_note?: boolean;
  credit_note_id?: string | null;
  credit_note_irn?: string | null;
  credit_note_total?: number | null;
  credit_note_issued_at?: string | null;
  credit_note_issued_at_label?: string | null;
  credit_note_reason?: string | null;
  credit_note_issued_by_name?: string | null;
}

/** Money strip over the WHOLE filtered set (not the page). */
export interface OrdersBrowserSummary {
  count: number;
  gross: number;
  credits: number;
  credits_count: number;
  pra_printed: number;
  /** Rs 0 cancels hidden by the current filters (0 when they are included). */
  empty_cancels: number;
}

export interface OrdersBrowserResponse {
  /** Single day (drawer) or the range start. */
  date: string;
  date_label: string;
  from: string;
  to: string;
  single_day: boolean;
  timezone: string;
  pra_window_days: number;
  orders: OrdersBrowserRow[];
  summary: OrdersBrowserSummary;
  pagination: { page: number; per_page: number; total: number };
}

/** F-RPT-N30 — every facet the Orders browser accepts. Mirrors
 *  backend parseOrdersBrowserQuery. */
export type OrdersBrowserPaymentFilter =
  | ''
  | 'cash'
  | 'card'
  | 'online'
  | 'gift_card'
  /** F-MM-N30: any house-account bill, or just one kind. */
  | 'house_account'
  | 'staff_meal'
  | 'tab';
export type OrdersBrowserStatusFilter = '' | 'completed' | 'credit_noted' | 'cancelled' | 'open';
export type OrdersBrowserSortKey =
  | 'time'
  | 'date'
  | 'total'
  | 'order_number'
  | 'table'
  | 'server'
  | 'payment'
  | 'order_type'
  | 'status';

export interface OrdersBrowserParams {
  /** Exactly one of `date` or `from`+`to`. */
  date?: string;
  from?: string;
  to?: string;
  search?: string;
  order_types?: string[];
  payment_method?: OrdersBrowserPaymentFilter;
  payment_sub_method?: string;
  status?: OrdersBrowserStatusFilter;
  pra_filter?: 'all' | 'printed' | 'not_printed' | 'eligible';
  fiscal_filter?: 'all' | 'synced' | 'pending' | 'failed' | 'exempt' | 'none';
  sort?: OrdersBrowserSortKey;
  dir?: 'asc' | 'desc';
  page?: number;
  per_page?: number;
  include_empty_cancels?: boolean;
  /** Deep-linked order id: always included and pinned to the top of page 1. */
  focus?: string;
}

/** One cross-day match from the Daily Sales invoice search (backend
 *  SearchInvoices) — by order number, PRA invoice number, FBR IRN, or customer.
 *  `business_date` is the drill-in target so the UI can open the full drawer. */
export interface InvoiceSearchRow {
  id: string;
  order_number: string;
  business_date: string; // YYYY-MM-DD
  created_at: string;
  customer_name?: string;
  total_amount: number;
  status: string;
  checkout_payment_method?: string;
  fiscal_irn?: string;
  fiscal_status?: string;
  pra_invoice_number?: string;
}

/** Refund Logs per-day rollup row (backend GetRefundsDaily). Buckets credit
 *  notes by their issue date in the configured business timezone — refunding
 *  a 3-day-old order shows under today's row, not 3 days ago. */
export interface RefundDailyRow {
  date: string;
  date_label: string;
  refunds: number;
  gross_refund: number;
  subtotal_refund: number;
  tax_refund: number;
  synced: number;
  pending: number;
  failed: number;
}

/** One credit note in the per-day Refund Logs drill (backend GetRefundsByDay). */
export interface RefundLogRow {
  id: string;
  order_id: string;
  order_number: string;
  original_order_business_date?: string | null;
  created_at: string;
  created_at_label: string;
  total_amount: number;
  subtotal: number;
  tax_amount: number;
  reason?: string | null;
  status: 'draft' | 'pending' | 'synced' | 'failed' | string;
  fiscal_irn?: string | null;
  fiscal_authority?: string | null;
  issued_by_name?: string | null;
  items_count: number;
}

export type ReportsExportId =
  | 'orders'
  | 'overview'
  | 'daily_sales'
  | 'hourly'
  | 'items'
  | 'tables'
  | 'party_size'
  | 'refunds'
  | 'tax'
  | 'staff'
  | 'adjustments';

// Tax / FBR output-tax summary (Reports → Tax tab)
export interface TaxRateBand {
  rate: number;
  orders: number;
  taxable_base: number;
  output_tax: number;
}

export interface TaxReconciliation {
  charged_tax: number;
  submitted_tax: number;
  submitted_count: number;
  delta_charged_minus_c: number;
  credit_note_tax: number;
  credit_note_tax_synced: number;
  credit_notes: number;
  net_output_tax: number;
  queue_pending: number;
  queue_dead: number;
}

export interface TaxSummaryReport {
  bands: TaxRateBand[];
  total_taxable_base: number;
  total_output_tax: number;
  reconciliation: TaxReconciliation;
  timezone: string;
  /** F-MM-N23: marketplace-settled orders — untaxed by design, outside every band. */
  marketplace_settled?: {
    orders: number;
    gross: number;
    service_charge: number;
    /** Memo estimate from the order type's commission %; never booked. */
    commission_est: number;
  };
}

// Staff / server performance (Reports → Staff tab)
export interface StaffPerfRow {
  server: string;
  orders: number;
  covers: number;
  gross: number;
  net: number;
  avg_check: number;
  discounts: number;
  /** This person's share of collected service charges (F-RPT-N17) —
   *  distributed to staff at day close. Excludes fully credit-noted orders. */
  service_charge: number;
  cancelled_orders: number;
  cancelled_value: number;
}

export interface StaffPerformanceReport {
  rows: StaffPerfRow[];
  timezone: string;
}

// Kitchen Types
export interface KitchenOrder {
  id: string;
  order_number: string;
  table_id?: string;
  table_number?: string;
  order_type: string;
  status: string;
  customer_name?: string;
  created_at: string;
  items?: OrderItem[];
}

// Table Status Types
export interface TableStatus {
  total_tables: number;
  occupied_tables: number;
  available_tables: number;
  occupancy_rate: number;
  by_location: LocationStats[];
}

export interface LocationStats {
  location: string;
  total_tables: number;
  occupied_tables: number;
  available_tables: number;
  occupancy_rate: number;
}

// Store Inventory Types
export interface StockCategory {
  id: string;
  name: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  item_count?: number;
  /** Archived items (kept for history) — they block hard-deleting the category. */
  archived_item_count?: number;
}

export interface StockItem {
  id: string;
  category_id?: string;
  name: string;
  unit: string;
  quantity_on_hand: number;
  reorder_level: number;
  default_unit_cost?: number;
  /** Moving weighted-average cost, when receipts have established one. */
  wac_unit_cost?: number | null;
  /** Primary vendor for this item (drives vendor-wise reports). */
  supplier_id?: string | null;
  supplier_name?: string;
  notes?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  category?: StockCategory;
  /** Next expiry among open lots (YYYY-MM-DD), when tracked */
  earliest_expiry?: string;
  /** Days from the business date to earliest_expiry; negative = expired.
   *  Server-computed, so every screen agrees on what "today" is. */
  earliest_expiry_days?: number;
  /** Expiry tracking (migration 147): a tracked item needs an expiry date on
   *  every receipt; shelf life (days) prefills it. */
  tracks_expiry?: boolean;
  shelf_life_days?: number | null;
  /** Server-computed status — the ONE definition (oversold | out | low | ok).
   *  Never recompute client-side; that is how three surfaces disagreed. */
  severity?: 'oversold' | 'out' | 'low' | 'ok';
  /** Undelivered quantity on open POs ("Low · 40 on order"). */
  on_order_qty?: number;
  /** How it is bought vs stocked (migration 118): "Bag 25 kg" holds 25 × unit. */
  purchase_unit_label?: string | null;
  purchase_unit_factor?: number | null;
  /** Equipment that goes out to an event and must come back (migration 133). */
  is_returnable?: boolean;
}

// ── Kitchen flow Lane A — item master ───────────────────────────────────────

/** One barcode on a stock item; pack_qty base units per scan. */
export interface StockItemBarcode {
  id: string;
  barcode: string;
  stock_item_id: string;
  pack_qty: number;
  pack_label?: string | null;
  created_at: string;
}

/** GET /store/barcodes/:code */
export interface BarcodeMatch {
  barcode: string;
  pack_qty: number;
  pack_label?: string | null;
  item: {
    id: string;
    name: string;
    unit: string;
    quantity_on_hand: number;
    is_active: boolean;
    category_name?: string | null;
    purchase_unit_label?: string | null;
    purchase_unit_factor?: number | null;
  };
}

/** GET /store/barcodes/:code/lookup — Open Food Facts pre-fill (suggestion only). */
export interface BarcodeExternalInfo {
  barcode: string;
  found: boolean;
  source: string;
  name?: string;
  brand?: string;
  quantity_label?: string;
  image_url?: string;
  cached: boolean;
}

/** One open lot, in the order stock is drawn (earliest expiry first). */
export interface StockLot {
  id: string;
  lot_no?: string | null;
  quantity_remaining: number;
  expiry_date?: string | null;
  days_until_expiry?: number | null;
  received_at: string;
  unit_cost?: number | null;
  /** Where the lot sits, when locations are on. */
  location_name?: string | null;
}

/** An open lot of a tracked item with no expiry date (F-CTRL-N73). */
export interface UndatedLot {
  batch_id: string;
  stock_item_id: string;
  item_name: string;
  unit: string;
  quantity_remaining: number;
  /** Business date the lot arrived, YYYY-MM-DD. */
  received_at: string;
  lot_no?: string | null;
  location_name?: string | null;
  shelf_life_days?: number | null;
  /** received_at + shelf life, when the item has one. */
  suggested_expiry?: string | null;
  unit_cost?: number | null;
}

/** The "expiring soon" window (F-CTRL-N73). */
export interface ExpirySettings {
  alert_days: number;
  choices: number[];
}

export interface WriteOffLotResult {
  movement_id?: string;
  stock_item_id: string;
  quantity: number;
  value: number;
}

export interface StockLotsResponse {
  location_id?: string | null;
  lots: StockLot[];
  /** The lot to pick first, when there is a choice or a date to beat. Never an expired lot. */
  use_first?: StockLot | null;
  /** Lots already past their expiry — to throw away (pilot fix A2). */
  expired_qty?: number;
  expired_first?: string | null;
  expired_last?: string | null;
  /** Kitchen flow on: the place's balance and name (the store's shelf by default). */
  location_balance?: number | null;
  location_name?: string | null;
}

/** A delivery-slip photo on a purchase order (metadata only). */
export interface PurchaseOrderAttachment {
  id: string;
  kind: string;
  content_type: string;
  size_bytes: number;
  uploaded_by_name?: string | null;
  created_at: string;
}

export interface StockImportRow {
  row: number;
  name: string;
  category?: string;
  category_is_new?: boolean;
  unit: string;
  purchase_unit_label?: string | null;
  purchase_unit_factor?: number | null;
  barcodes: { barcode: string; pack_qty: number }[];
  cost?: number | null;
  reorder_level: number;
  supplier?: string;
  opening_qty: number;
  is_prep: boolean;
  /** Expiry tracking (migration 147). opening_expiry is YYYY-MM-DD. */
  tracks_expiry?: boolean;
  shelf_life_days?: number | null;
  opening_expiry?: string;
  notes?: string;
  errors: string[];
}

export interface StockImportResult {
  dry_run: boolean;
  committed: boolean;
  total_rows: number;
  valid_rows: number;
  error_rows: number;
  created: number;
  new_categories: string[];
  warnings: string[];
  rows: StockImportRow[];
}

export interface Supplier {
  id: string;
  name: string;
  contact_name?: string;
  phone?: string;
  email?: string;
  notes?: string;
  /** House commission % kept per vendor. Vendor payout = sale × (100 − this). */
  commission_percent?: number;
  /** Consignment vendor: PO receipts don't bill cost; payable comes from sales settlement. */
  is_consignment?: boolean;
  /** Unused supplier-credit returns (credit notes on the account). Nets against open bills (F-MM-N21). */
  credit_balance?: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Vendor-wise (consignment) reports.
export interface VendorSalesRow {
  item_name: string;
  qty_sold: number;
  avg_unit_price: number;
  gross_sales: number;
  house_cut: number;
  vendor_payout: number;
  /** F-RPT-N19. cost = the commission payable (consignment) or the COGS
   *  snapshot (bought outright). `costed` false means no cost was recorded —
   *  render margin blank, never as the full gross. */
  cost?: number;
  margin?: number;
  margin_pct?: number;
  costed?: boolean;
}
export interface VendorSettlement {
  bill_id: string;
  amount: number;
  status: string;
}
/** F-RPT-N19: a vendor's own stock consumed inside a dish that also draws on
 *  other suppliers. Carries no revenue on purpose — the dish's retail belongs
 *  to the dish, and splitting it would double-count across vendors. */
export interface VendorUsageRow {
  item_name: string;
  unit: string;
  qty_consumed: number;
  /** Dishes it went into, comma-joined. */
  used_in: string;
}

export interface VendorSalesReport {
  vendor_name: string;
  commission_percent: number;
  is_consignment: boolean;
  from: string;
  to: string;
  items: VendorSalesRow[];
  total_gross: number;
  total_house_cut: number;
  total_vendor_payout: number;
  /** F-RPT-N19 — one Cost/Margin pair, two sources. Consignment: cost is the
   *  commission payable and margin is the house cut. Bought outright: cost is
   *  the COGS snapshot and margin is gross − cost. Optional so a backend
   *  predating this still parses. */
  total_cost?: number;
  total_margin?: number;
  total_margin_pct?: number;
  has_cost_data?: boolean;
  /** Ingredient consumption in multi-vendor dishes. NOT part of total_gross. */
  composed_usage?: VendorUsageRow[];
  /** Existing consignment settlement bill for this exact period, if any. */
  settlement: VendorSettlement | null;
}
export interface VendorStockRow {
  item_name: string;
  unit: string;
  qty_on_hand: number;
  unit_cost: number;
  stock_value: number;
  /** On-hand went negative (oversold) — a reconcile cue, not real negative units. */
  oversold?: boolean;
}
export interface VendorStockReport {
  vendor_name: string;
  items: VendorStockRow[];
  total_qty: number;
  total_value: number;
}

export interface PurchaseOrderSummary {
  id: string;
  supplier_id: string;
  supplier_name: string;
  document_type: 'purchase_order';
  status: string;
  expected_date?: string;
  ship_date?: string;
  receipt_date?: string;
  notes?: string;
  created_at: string;
  total_ordered_qty: number;
  line_count?: number;
  /** Plan value: SUM(qty ordered × line unit cost). */
  ordered_value?: number;
  /** Ledger value: SUM of non-void purchase movements for this PO. */
  received_value?: number;
  bill_count?: number;
  bill_amount?: number;
  bill_outstanding?: number;
  /** Rolled up across the PO's bills: draft | open | partially_paid | paid. */
  bill_status?: string | null;
  /** Kitchen flow pilot (Batch 5): who made the order (self-approval rule). */
  created_by?: string;
}

/** One vendor bill linked to a PO (detail payload; void bills included). */
export interface PurchaseOrderBillRef {
  id: string;
  invoice_number?: string;
  amount: number;
  amount_paid: number;
  outstanding: number;
  status: string;
  bill_date?: string;
  due_date?: string;
}

/** One received delivery line, from the non-void purchase stock movements. */
export interface PurchaseOrderReceiptEvent {
  movement_id: string;
  item_name: string;
  unit: string;
  quantity: number;
  unit_cost?: number;
  total_cost?: number;
  created_at: string;
}

/** What a receive / quick purchase reports back — including the AP side. */
export interface PoReceiveResult {
  po_status?: string;
  closed_short?: boolean;
  received_value?: number;
  bill?: { id: string; amount: number; due_date?: string; status: string } | null;
  bill_skipped_reason?: string | null;
  idempotent_replay?: boolean;
}

export interface QuickPurchaseResult extends PoReceiveResult {
  purchase_order_id?: string;
  status?: string;
  supplier_name?: string;
}

export interface PurchaseOrderLine {
  id: string;
  stock_item_id: string;
  item_name: string;
  unit: string;
  quantity_ordered: number;
  unit_cost?: number;
  quantity_received: number;
  quantity_returned?: number;
  quantity_returnable?: number;
  /** Kitchen flow pilot (Batch 5): this delivery's stock no longer in the
   *  store (moved to the kitchen). Returns only take from the store. */
  quantity_elsewhere?: number;
  /** Kitchen flow pilot (Batch 5): the pack the line is counted in — the
   *  vendor's catalogue pack, else the item's buying unit. */
  pack_label?: string | null;
  pack_qty?: number | null;
  /** Kitchen flow pilot (Batch 5): what the deliveries actually cost per unit
   *  (may differ from unit_cost, the approved price). */
  received_unit_cost?: number | null;
  /** The item's buying unit (migration 118): receiving shows "2 × Bag 25 kg". */
  purchase_unit_label?: string | null;
  purchase_unit_factor?: number | null;
  /** The item's expiry tracking (migration 147): a tracked line needs a date. */
  tracks_expiry?: boolean;
  shelf_life_days?: number | null;
}

export interface PurchaseOrderDetail {
  id: string;
  document_type: 'purchase_order';
  status: string;
  supplier_id: string;
  supplier_name: string;
  expected_date?: string;
  ship_date?: string;
  receipt_date?: string;
  notes?: string;
  created_at: string;
  ordered_value?: number;
  received_value?: number;
  lines: PurchaseOrderLine[];
  bills?: PurchaseOrderBillRef[];
  receipts?: PurchaseOrderReceiptEvent[];
  /** Kitchen flow lane D: set when the PO was raised for a requisition shortfall. */
  source_requisition_id?: string;
  source_requisition_no?: string;
  /** Kitchen flow lane D: 'petty_cash' for a quick purchase paid in cash;
   *  F-CTRL-N75: 'till' for one paid with cash from the drawer. */
  paid_from?: 'vendor_credit' | 'petty_cash' | 'till';
  /** F-CTRL-N75: set only on a purchase already bought and paid for. */
  bought_paid_by?: 'till' | 'petty_cash' | 'buyer' | 'company';
  bought_buyer_name?: string;
  bought_stall?: string;
  bought_delivered_to?: 'store' | 'kitchen';
  /** The Day Close cash-out behind a till buy. */
  cash_movement_id?: string;
  /** Kitchen flow pilot (Batch 5): who made the order (self-approval rule). */
  created_by?: string;
}

export interface VendorBill {
  id: string;
  supplier_id: string;
  supplier_name: string;
  purchase_order_id?: string;
  invoice_number?: string;
  bill_date?: string;
  due_date?: string;
  amount: number;
  amount_paid: number;
  outstanding: number;
  status: 'draft' | 'open' | 'partially_paid' | 'paid' | 'void';
  notes?: string;
  created_at: string;
  /** F-MM-N24: the balance brought forward when the tenant migrated onto Bhookly — one per vendor. */
  is_opening_balance?: boolean;
  /** F-FE-N128: set on a consignment settlement — the sales period this payout covers. */
  settlement_from?: string;
  settlement_to?: string;
}

export interface VendorLedgerEntry {
  date: string;
  type: 'bill' | 'payment' | 'return';
  description: string;
  debit: number;
  credit: number;
  balance: number;
}

export interface StockMovement {
  id: string;
  /** Absent on grouped sale events (the row represents the whole order). */
  stock_item_id?: string;
  movement_type: 'purchase' | 'issue' | 'adjustment' | 'supplier_return' | 'sale' | 'sale_reversal' | 'opening_balance' | 'transfer_in' | 'transfer_out' | 'production_consume' | 'production_output' | 'transfer_variance' | 'merge_in' | 'merge_out';
  quantity: number;
  unit_cost?: number;
  total_cost?: number;
  issued_to_user_id?: string;
  created_by?: string;
  note?: string;
  created_at: string;
  item_name?: string;
  item_unit?: string;
  issued_to_name?: string;
  created_by_name?: string;
  /** Present when a purchase was reversed (ledger row kept for audit) */
  voided_at?: string;
  void_reason?: string;
  voided_by_name?: string;
  /** Server: manual purchase with an intact lot — void / cost correction allowed */
  purchase_can_void?: boolean;
  /** Links the UI uses to say what actually happened, in plain language. */
  stock_count_id?: string;
  order_id?: string;
  order_number?: string;
  purchase_order_id?: string;
  supplier_name?: string;
  /** Grouped-sale events: number of component deductions and what they were. */
  line_count?: number;
  lines?: { item_name: string; quantity: number; unit: string }[];
}

/** Append-only row from GET /store/inventory-activity */
export interface InventoryActivityEntry {
  id: string;
  created_at: string;
  actor_id?: string;
  actor_name: string;
  action: string;
  entity_type: string;
  entity_id?: string;
  summary: string;
  metadata?: Record<string, unknown>;
}

export interface StockAlert {
  id: string;
  name: string;
  code?: string;
  unit: string;
  quantity_on_hand: number;
  reorder_level: number;
  category_id?: string;
  category_name: string;
  supplier_id?: string;
  supplier_name?: string;
  default_unit_cost: number;
  wac_unit_cost?: number;
  /** Undelivered quantity across open POs (pending_approval/approved/ordered/partially_received). */
  on_order_qty: number;
  /** Earliest expected_date among those open POs (YYYY-MM-DD). */
  next_expected_date?: string;
  /** Average consumption per day over the last 30 days (sales + issues, net of reversals). */
  avg_daily_usage: number;
  /** on_hand / avg_daily_usage; absent when there is no recent usage. */
  days_cover?: number;
  last_purchase_at?: string;
  last_purchase_cost?: number;
  severity: 'oversold' | 'out' | 'low';
  /** Order-this-much proposal: max(2×reorder, 14d usage) − on hand − on order. */
  suggested_qty: number;
}

export interface ExpiringBatchAlert {
  batch_id: string;
  stock_item_id: string;
  item_name: string;
  unit: string;
  quantity_remaining: number;
  unit_cost: number;
  expiry_date: string;
  /** Negative = already expired. */
  days_until_expiry: number;
  value_at_risk: number;
  lot_no?: string | null;
}

export interface UserBrief {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
}

export interface StockSummary {
  overview: {
    total_items: number;
    total_value: number;
    low_stock_count: number;
    /** Active items with no reorder level set (kitchen-flow Store banner). */
    no_level_count?: number;
    /** Lots inside the expiring-soon window, not yet expired (F-CTRL-N73). */
    expiring_lot_count?: number;
    expired_lot_count?: number;
    expiry_value_at_risk?: number;
    expiry_alert_days?: number;
  };
  categories: {
    category_name: string;
    total_items: number;
    total_value: number;
    low_stock_count: number;
  }[];
  weekly_usage: {
    week: string;
    purchase_qty: number;
    issue_qty: number;
    purchase_cost: number;
  }[];
}

/**
 * One stock item seen three ways (F-CTRL-N57). `on_hand` is what every screen
 * values; `ledger_net` is what its movement history alone would leave;
 * `batch_qty` is what the FIFO cost layers hold. They should agree.
 */
export interface StockReconciliationRow {
  item_id: string;
  item_name: string;
  unit: string;
  category: string;
  on_hand: number;
  ledger_net: number;
  movements: number;
  batch_qty: number;
  unit_cost: number;
  /** on_hand the ledger cannot account for — a quantity written with no movement. */
  unexplained: number;
  unexplained_value: number;
  /** on_hand minus the FIFO layers. */
  batch_drift: number;
}

export interface StockReconciliationReport {
  summary: {
    items_total: number;
    items_diverging: number;
    unexplained_value_total: number;
  };
  items: StockReconciliationRow[];
}

/**
 * The Reports tab payload (F-BE-N51 rewrite): four plain-language sections —
 * what stock is worth now, what moved this period, what was lost, and what
 * needs attention. Usage figures include recipe-driven sale depletion plus
 * manual issues; every value is PKR at stored movement cost with a current-
 * cost fallback for legacy rows.
 */
export interface AdvancedStockReport {
  window: { from: string; to: string; period_days: number };
  stock_now: {
    total_value: number;
    categories: { category_id: string | null; name: string; value: number; pct: number }[];
    /** Items whose book quantity is below zero — only a count fixes them. */
    negative_items: { item_id: string; name: string; unit: string; on_hand: number; value_impact: number }[];
    no_reorder_point_count: number;
  };
  activity: {
    /** Sold depletion (net of refunds) + kitchen/staff issues, valued. */
    used_value: number;
    sold_depletion_value: number;
    kitchen_issue_value: number;
    purchased_value: number;
    /** used ÷ current stock value × 100; null when nothing was used. */
    used_pct_of_stock_value: number | null;
    /** Stock value ÷ average daily usage; null when nothing was used. */
    days_of_stock_left: number | null;
    trend: { bucket_start: string; label: string; purchase_value: number; usage_value: number }[];
    trend_granularity: 'day' | 'week';
  };
  losses: {
    /** Spoilage + damage write-downs (vendor returns and count corrections excluded). */
    waste_value: number;
    /** waste ÷ (waste + used) × 100; null when nothing left the shelf. */
    waste_pct: number | null;
    waste_rows: {
      item_name: string;
      category: string;
      unit: string;
      qty: number;
      reason: string;
      value: number;
      occurred_at: string;
      source: 'issue' | 'adjustment' | string;
    }[];
    /** Full count in the window; waste_rows is capped at the top 50 by value. */
    waste_rows_total: number;
    /** Missing stock found by physical counts (positive number). */
    count_shrinkage_value: number;
    /** Extra stock found by physical counts — reported separately, never netted. */
    count_found_value: number;
    count_rows: {
      item_id: string;
      item_name: string;
      unit: string;
      category: string;
      counted_at: string;
      counted_qty: number;
      book_qty_before: number;
      variance_qty: number;
      variance_value: number;
      /** True for pre-migration counts valued at current cost rather than the lots actually adjusted. */
      estimated: boolean;
      prev_count_at: string | null;
      sold_since_prev: number | null;
      wasted_since_prev: number | null;
      purchased_since_prev: number | null;
    }[];
    count_rows_total: number;
    /** Stock sent back to vendors — money coming back, not loss. */
    vendor_returns_value: number;
    /** F-CTRL-N74: dishes thrown away from voids and refunds. Their
     *  ingredients are already inside waste_value. */
    order_waste?: {
      void_count: number;
      void_cost: number;
      void_menu_value: number;
      refund_count: number;
      refund_cost: number;
      refund_menu_value: number;
    };
  };
  attention: {
    low_stock_count: number;
    out_of_stock_count: number;
    negative_stock_count: number;
    count_recommendations: {
      item_id: string;
      name: string;
      unit: string;
      on_hand: number;
      value: number;
      reason: 'never_verified' | 'stale_count' | 'negative_stock';
      last_counted_at: string | null;
      days_since_count: number | null;
    }[];
  };
}

// Expense Types
/** Stored on each expense row; must match an `expense_category_defs.slug` (active) when creating/updating. */
export type ExpenseCategory = string;

/** Admin-managed catalog row (GET/POST/PUT/DELETE `/admin/expense-category-definitions`). */
export interface ExpenseCategoryDefinition {
  id: string;
  slug: string;
  label: string;
  color: string;
  sort_order: number;
  is_system: boolean;
  is_active: boolean;
  /**
   * Marks the category as STOCK rather than an operating expense (F-RPT-N22).
   * Spend recorded under it is excluded from operating expenses, because its
   * cost reaches the P&L as COGS when the goods sell — counting both charges
   * the business twice for the same purchase.
   *
   * Editing this affects only expenses recorded afterwards: each row stamps its
   * own treatment at creation, so past reports cannot move.
   */
  is_inventory?: boolean;
}

export interface Expense {
  id: string;
  category: ExpenseCategory;
  amount: number;
  description?: string;
  reference_type?: string;
  reference_id?: string;
  expense_date: string;
  /** When the expense was recorded (use for ledger date+time display). */
  recorded_at?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  created_by_name?: string;
  /** Migration 108 (F-RPT-N35): who was paid, how, against which document. */
  vendor_id?: string | null;
  vendor_name?: string | null;
  payee?: string | null;
  payment_method?: ExpensePaymentMethod | null;
  reference_no?: string | null;
  notes?: string | null;
  /** Stamped accounting treatment: true = stock (reaches the P&L as COGS). */
  excluded_from_opex?: boolean;
  /** Set when the row was approved from a scanned receipt (F-RPT-N43). */
  receipt_scan_id?: string | null;
  /** F-CTRL-N79: a Day Close paid-out an admin may correct from the ledger. */
  editable_linked?: boolean;
  /** F-CTRL-N79: the paid-out's business day is sealed — only the category can change. */
  linked_day_closed?: boolean;
}

export type ExpensePaymentMethod = 'cash' | 'bank' | 'card' | 'other';

export interface DailyClosing {
  id: string;
  closing_date: string;
  total_sales: number;
  total_tax: number;
  total_orders: number;
  cash_sales: number;
  card_sales: number;
  /** Online tender bucket (cash | card | online — see PaymentMethod). */
  online_sales: number;
  total_expenses: number;
  net_profit: number;
  opening_cash: number;
  expected_cash: number;
  actual_cash?: number;
  cash_difference?: number;
  notes?: string;
  closed_by?: string;
  created_at: string;
  closed_by_name?: string;
}

/** F-RPT-N41: how a tenant reports cost of goods. */
export type CogsBasis = 'at_sale' | 'purchases'
export type CogsBasisWindow = CogsBasis | 'mixed'
export interface CogsBasisPeriod {
  effective_from: string
  basis: CogsBasis
  set_by_name: string
  set_at: string
}
/** F-RPT-N42: one admin action that counted categories as stock on the purchases basis. */
export interface CogsStockClassification {
  applied_from: string
  slugs: string[]
  rows_moved: number
  amount_moved: number
  set_by_name: string
  set_at: string
}
export interface CogsBasisState {
  basis: CogsBasis
  /** '' when no switch has ever been made (the at_sale default). */
  effective_from: string
  /** The current business date, YYYY-MM-DD. */
  today: string
  periods: CogsBasisPeriod[]
  /** Oldest first. Absent on a backend predating F-RPT-N42. */
  classifications?: CogsStockClassification[]
}

export interface PnLRow {
  period: string;
  revenue: number;
  tax: number;
  orders: number;
  expenses: number;
  /** Cost of goods sold for the bucket. Always subtracted from net_profit;
   *  optional here so a backend predating F-RPT-N18 still parses. */
  cogs?: number;
  cogs_purchases?: number;
  net_profit: number;
  /** F-RPT-N29: the full ladder per bucket, matching the summary tile. */
  pos_fees?: number;
  net_sales?: number;
  gross_profit?: number;
  refunds?: number;
  /** F-RPT-N50: the sales tax inside `refunds`, already out of net_profit with tax. */
  refunds_tax?: number;
  net_profit_after_refunds?: number;
  /** F-MM-N23: estimated marketplace commission for the bucket (memo only). */
  platform_commission_est?: number;
  /** F-MM-N30: food cost of staff meals, already subtracted in net_profit. */
  staff_meals_cost?: number;
}

export interface PnLReport {
  period: string;
  from: string;
  to: string;
  rows: PnLRow[];
  summary: {
    total_revenue: number;
    total_tax: number;
    total_orders: number;
    total_expenses: number;
    net_profit: number;
    /** F-RPT-N18: the ladder rungs between revenue and net profit. The backend
     *  has always sent these; without them declared, nothing rendered COGS and
     *  "revenue − expenses = net profit" looked broken on screen. */
    net_sales?: number;
    cogs?: number;
    /** F-RPT-N41: cogs = cogs_at_sale + cogs_purchases. */
    cogs_at_sale?: number;
    cogs_purchases?: number;
    cogs_basis?: CogsBasisWindow;
    gross_profit?: number;
    cost_coverage_pct?: number;
    has_cogs_data?: boolean;
    /** F-RPT-N1: refunds issued in the period (magnitude). */
    refunds_issued?: number;
    /** F-RPT-N50: the sales tax inside refunds_issued, already out of net_profit with tax. */
    refunds_tax?: number;
    /** NetProfit − (RefundsIssued − RefundsTax) (F-RPT-N1, F-RPT-N50). Optional for backward compat. */
    net_profit_after_refunds?: number;
    /** F-RPT-03: rungs the ladder needs to balance on screen. */
    pos_fees?: number;
    service_charge?: number;
    discounts?: number;
    /** F-MM-N23: marketplace-settled sales in the period (already inside revenue). */
    marketplace_sales?: number;
    /** F-MM-N23: estimated marketplace commission — memo only, never booked. */
    marketplace_commission_est?: number;
    /** F-MM-N23: net_profit_after_refunds − marketplace_commission_est. */
    net_profit_after_platform_fees_est?: number;
    /** F-MM-N30: "Staff meals (at cost)" — already subtracted in net_profit. */
    staff_meals_cost?: number;
    /** F-RPT-N49: the meals behind that line and their menu value… */
    staff_meal_orders?: number;
    staff_meal_value?: number;
    /** …and the part of that value carrying no cost: no recipe, or a day on the
     *  purchases basis (its food was costed when bought). Never estimated. */
    staff_meal_value_no_recipe?: number;
    staff_meal_value_in_purchases?: number;
  };
  expense_breakdown: { category: string; total: number }[];
}

export interface CurrentDayStatus {
  is_closed: boolean;
  date: string;
  total_sales: number;
  total_tax: number;
  total_orders: number;
  cash_sales: number;
  card_sales: number;
  /** Online tender bucket (cash | card | online — see PaymentMethod). */
  online_sales: number;
  /** F-MM-N24: bills settled from a gift card balance. Never part of "money
   *  you hold tonight" — that money arrived when the card was sold — but shown
   *  on Day Close so the tender tiles visibly account for the day's sales.
   *  Optional: a backend predating the feature omits it. */
  gift_card_sales?: number;
  /** F-MM-N30: tab bills charged today — sold, not collected. */
  house_account_sales?: number;
  /** F-MM-N30: tab payments received today, already inside the expected
   *  cash / card / online the closer counts. */
  tab_settlements_cash?: number;
  tab_settlements_card?: number;
  tab_settlements_online?: number;
  tab_settlements_total?: number;
  /** F-MM-N30: staff meals today — not sales; cost is the P&L line. */
  staff_meal_orders?: number;
  staff_meal_value?: number;
  staff_meals_cost?: number;
  /** Online provider split (migration 050) for reconciliation. Omitted when
   *  the feature is off / nothing attributed; amounts sum to online_sales. */
  online_submethods?: { slug: string; label: string; amount: number; count: number }[];
  total_expenses: number;
  /** F-RPT-N18: sent by the backend since the finance package landed, but never
   *  declared here — so the Expense Dashboard rendered gross sales, expenses
   *  and net profit while the COGS rung between them stayed invisible. */
  net_sales?: number;
  cogs?: number;
  gross_profit?: number;
  cost_coverage_pct?: number;
  has_cogs_data?: boolean;
  net_profit: number;
  expense_categories?: { category: string; total: number }[];
  /** Real open orders still holding tables/revenue (payable balance or a fired
   *  non-voided item). The Day Close wizard requires a note to lock when > 0
   *  (F-CTRL-N25). */
  unresolved_open_orders?: number;
  /** F-MM-N23: the part of online_sales the marketplace pays out later — a
   *  receivable, not money that landed tonight. Excluded from expected online. */
  platform_settled_online?: number;
  /** F-MM-N23: gross of marketplace-settled orders today. */
  marketplace_sales?: number;
  /** Count of marketplace-settled orders today (Day Close sales split). */
  marketplace_orders?: number;
  /** F-MM-N23: estimated marketplace commission today (memo only). */
  marketplace_commission_est?: number;
  /** Refunds issued today (magnitude). Sales tiles are gross; tender tiles net. */
  refunds_issued?: number;
}

export interface ExpenseSummary {
  /** Per category in the filter. `total` is everything filed under it; `stock_total` the part that is stock. */
  categories: { category: string; count: number; total: number; stock_total: number }[];
  /** Operating expenses only — what enters the P&L (F-RPT-N22). */
  grand_total: number;
  /** F-RPT-N37: stock purchases in the filter, excluded from opex (COGS at sale). */
  stock_total: number;
  /** F-RPT-N37: rows matched by the filter. */
  count: number;
  from: string;
  to: string;
}

// ───────────────────────────────────────────────────────────────────────────
// Day Operations — mirrors backend/internal/dayops/service.go.
// ───────────────────────────────────────────────────────────────────────────

export type DayOpsState = 'open' | 'staged' | 'closed' | 'reopened';

/**
 * One row from the `business_days` table. The optional fields fill in
 * progressively as the day moves through the lifecycle: opening_cash on
 * open, counted_* on stage-close, closed_at on lock-close.
 */
export interface BusinessDay {
  id: string;
  business_date: string;
  status: DayOpsState;
  opened_at: string;
  opened_by?: string | null;
  opened_by_name?: string | null;
  opening_cash: number;
  opening_notes?: string | null;
  staged_at?: string | null;
  staged_by?: string | null;
  staged_by_name?: string | null;
  closed_at?: string | null;
  closed_by?: string | null;
  closed_by_name?: string | null;
  counted_cash?: number | null;
  counted_card?: number | null;
  counted_online?: number | null;
  expected_cash?: number | null;
  cash_variance?: number | null;
  /**
   * Card / online reconciliation, mirroring the cash columns above. The backend
   * has persisted these since F-CTRL-N17 but nothing on the frontend declared
   * them, so no screen could read them and the card/online variance was
   * invisible everywhere outside the close wizard itself (F-CTRL-N33).
   *
   * `expected_*` is the machine / bank batch we expect: net sales for the day
   * plus cross-day tender swiped today against earlier orders.
   *
   * NULL means **not counted** — only reachable via force-close, where nobody
   * attended the drawer. Render it as "—", never as Rs 0: a force-closed day
   * must not impersonate a counted-zero day.
   */
  expected_card?: number | null;
  expected_online?: number | null;
  card_variance?: number | null;
  online_variance?: number | null;
  /**
   * F-CTRL-N67: marketplace (Foodpanda) reconciliation. `expected_marketplace`
   * is the POS's marketplace-settled online tender for the day (the receivable
   * kept out of `expected_online`); `counted_marketplace` is the partner-app
   * total the manager entered; `marketplace_variance` is counted − expected and
   * NULL when not counted (no marketplace sales, older bundle, force-close).
   */
  expected_marketplace?: number | null;
  counted_marketplace?: number | null;
  marketplace_variance?: number | null;
  gross_sales?: number | null;
  net_sales?: number | null;
  tax_collected?: number | null;
  discounts?: number | null;
  cogs?: number | null;
  expenses?: number | null;
  net_profit?: number | null;
  order_count?: number | null;
  closing_notes?: string | null;
}

export interface CashDrawerMovement {
  id: string;
  business_day_id: string;
  movement_type: 'paid_in' | 'paid_out';
  amount: number;
  reason: string;
  notes?: string | null;
  created_by?: string | null;
  created_by_name?: string | null;
  created_at: string;
  /** F-CTRL-N75: the expense category it was filed under. */
  expense_category?: string | null;
  /** Filed under a stock category (or paid for a purchase order). */
  is_stock?: boolean;
  /** The received purchase order this cash paid for. */
  purchase_order_id?: string | null;
  /** A stock cash-out whose items were never added: "stock not added". */
  needs_items?: boolean;
}

/**
 * One shift close — an intra-day drawer reconciliation at handover
 * (F-CTRL-N29). Mirrors backend/internal/dayops/shift_close.go's ShiftClosing.
 * Present only for tenants with closings_per_day > 1; the day-level close and
 * the once-per-day Z-report are unchanged, with shifts rolled up as detail.
 */
export interface ShiftClosing {
  id: string;
  business_day_id: string;
  shift_number: number;
  window_start: string;
  closed_at: string;
  closed_by?: string | null;
  closed_by_name?: string | null;
  opening_cash: number;
  counted_cash: number;
  expected_cash: number;
  cash_variance: number;
  counted_card?: number | null;
  counted_online?: number | null;
  /** Cash moved to the safe at handover (0 = none). */
  cash_drop: number;
  shift_cash_sales: number;
  shift_card_sales: number;
  shift_online_sales: number;
  shift_gross_sales: number;
  shift_order_count: number;
  notes?: string | null;
  created_at: string;
}

export interface DayCloseAuditEntry {
  id: string;
  business_day_id?: string | null;
  business_date: string;
  action:
    | 'open_day'
    | 'auto_open_day'
    | 'cash_movement'
    | 'stage_close'
    | 'lock_close'
    | 'shift_close'
    | 'shift_close_undo'
    | 'reopen';
  actor_id?: string | null;
  actor_name?: string | null;
  actor_role?: string | null;
  summary: string;
  metadata?: Record<string, unknown> | null;
  created_at: string;
}

/**
 * Z report payload — the bundle powering the printable end-of-day slip.
 * Mirrors backend/internal/dayops/zreport.go's ZReport struct. Generated
 * once on first request for a staged/closed day, then cached behind the
 * monotonic Z sequence so reprints are idempotent.
 */
export interface ZReportPayload {
  sequence: number;
  generated_at: string;
  day: BusinessDay;
  summary: ZReportFinanceSummary;
  cash_movements: CashDrawerMovement[];
  hourly: ZHourBucket[];
  voids: ZVoidEntry[];
  void_total: ZVoidsTotal;
  refunds: ZRefundEntry[];
  discounts: ZDiscountSummary;
  top_categories: ZCategoryRow[];
  top_items: ZItemRow[];
  /** Per-waiter split of the day's service charges (F-RPT-N17) — what the
   *  manager distributes from at close. [] when none; optional so a payload
   *  from an older backend still parses. */
  service_charge_by_staff?: ZStaffServiceChargeRow[];
  /** Per-shift close roll-up (F-CTRL-N29). Empty on single-close tenants. */
  shifts?: ShiftClosing[];
}

export interface ZReportFinanceSummary {
  from: string;
  to: string;
  gross_sales: number;
  sales_tax: number;
  discounts: number;
  /**
   * FBR PKR 1 per-invoice POS service fee collected over the period
   * (F-MM-N9). A liability like sales tax, not earnings — already netted out
   * of `net_sales`. Optional so a backend predating the field still parses;
   * 0 for opted-out and PRA tenants.
   */
  pos_fees_collected?: number;
  /** Service charge collected — distributed to staff, not revenue, so it is
   *  NOT a P&L ladder rung. Excludes orders fully reversed by credit note.
   *  Optional so a backend predating F-RPT-N17 still parses. */
  service_charge?: number;
  net_sales: number;
  cogs: number;
  gross_profit: number;
  operating_expenses: number;
  net_profit: number;
  refunds_issued: number;
  gross_sales_after_refunds: number;
  credit_note_tax: number;
  net_sales_tax: number;
  order_count: number;
  avg_ticket: number;
  guest_count: number;
  /** F-MM-N24 added `gift_card`; it is optional so a backend predating the
   *  tender still type-checks. `total` includes every bucket, so any consumer
   *  reconciling the mix must count the gift leg too. */
  payments: {
    cash: number;
    card: number;
    online: number;
    gift_card?: number;
    /** F-MM-N30: tab bills charged — sold, not collected tonight. */
    house_account?: number;
    total: number;
  };
  /** Online tender broken down by configured provider (migration 050).
   *  Omitted when the feature is off / nothing attributed. Amounts (incl. any
   *  "" / "Unattributed" bucket) sum to payments_gross.online. */
  online_submethods?: { slug: string; label: string; amount: number; count: number }[];
  /** F-MM-N30: tab payments received this day, already inside expected cash/card/online. */
  tab_settlements?: { cash: number; card: number; online: number; total: number };
  /** F-MM-N30: staff meals — never sales. Cost is subtracted in net_profit. */
  staff_meal_orders?: number;
  staff_meal_value?: number;
  staff_meals_cost?: number;
  cost_coverage_pct: number;
  expenses_enabled: boolean;
  /** F-MM-N23 — all optional; a backend predating the feature omits them. */
  marketplace_sales?: number;
  marketplace_orders?: number;
  /** Estimated commission from the order type's % — memo only, never booked. */
  marketplace_commission_est?: number;
  /** Online tender the platform settles later (receivable) — not in expected online. */
  platform_settled_online?: number;
  net_profit_after_platform_fees_est?: number;
  has_cogs_data: boolean;
}

export interface ZHourBucket {
  hour: number;
  gross: number;
  order_count: number;
}

export interface ZVoidEntry {
  item_name: string;
  quantity: number;
  unit_price: number;
  line_value: number;
  reason?: string | null;
  voided_by?: string | null;
  authorized_by?: string | null;
  created_at: string;
  /** F-CTRL-N74: absent on voids from before outcomes were recorded. */
  outcome?: 'not_made' | 'wasted' | null;
  waste_cost?: number | null;
}

export interface ZVoidsTotal {
  count: number;
  value: number;
  /** F-CTRL-N74 — optional so an older backend's payload still parses. */
  not_made_count?: number;
  not_made_value?: number;
  wasted_count?: number;
  wasted_value?: number;
  waste_cost?: number;
}

export interface ZRefundEntry {
  order_id: string;
  order_number?: string | null;
  amount: number;
  payment_method: string;
  processed_by?: string | null;
  processed_at: string;
}

export interface ZDiscountSummary {
  total: number;
  order_count: number;
  avg_percent: number;
}

export interface ZCategoryRow {
  name: string;
  quantity: number;
  net: number;
}

export interface ZItemRow {
  name: string;
  quantity: number;
  net: number;
}

export interface ZStaffServiceChargeRow {
  name: string;
  /** Orders by this person that actually carried a service charge. */
  order_count: number;
  amount: number;
}

/** GET /admin/reports/expense-intelligence — analytics for Expenses Intelligence tab */
export interface ExpenseIntelligenceKpis {
  total_sales: number;
  total_tax: number;
  total_orders: number;
  total_expenses: number;
  net_profit: number;
  expense_ratio: number;
  inventory_spend: number;
  inventory_to_sales_ratio: number;
  manual_expense_count: number;
  auto_linked_expense_count: number;
  prior_period_sales: number;
  prior_period_expenses: number;
  sales_change_pct: number;
  expenses_change_pct: number;
}

export interface ExpenseIntelligenceDayPoint {
  date: string;
  sales: number;
  expenses: number;
  net: number;
}

export interface ExpenseIntelligenceCategoryMix {
  category: string;
  total: number;
  pct: number;
}

export interface ExpenseIntelligenceCashStats {
  days_with_closing: number;
  avg_cash_difference: number;
  total_abs_cash_variance: number;
}

export interface ExpenseIntelligenceReport {
  period_days: number;
  from: string;
  to: string;
  kpis: ExpenseIntelligenceKpis;
  daily_trend: ExpenseIntelligenceDayPoint[];
  category_mix: ExpenseIntelligenceCategoryMix[];
  cash_closing_stats: ExpenseIntelligenceCashStats;
}

// Filter and Query Types
export interface OrderFilters {
  status?: string;
  order_type?: string;
  /**
   * When true, GET /orders returns only non-terminal tickets
   * (status NOT IN completed/cancelled). Used by the counter Open Orders
   * strip so completed volume cannot page open bills out of the result set.
   */
  open_only?: boolean;
  page?: number;
  per_page?: number;
  /** Legacy aliases — accepted for backward compat with the pos/ tree's PaymentHistory query. */
  limit?: number;
  offset?: number;
  /** Inclusive YYYY-MM-DD (server date on `orders.created_at`) */
  date_from?: string;
  /** Inclusive YYYY-MM-DD */
  date_to?: string;
}

/** Counter: update guest / CRM fields on an open order */
export interface UpdateCounterOrderGuestRequest {
  customer_name?: string;
  customer_email?: string;
  customer_phone?: string;
  guest_birthday?: string;
  /** Delivery destination; only sent for delivery orders. Empty string clears it. */
  delivery_address?: string;
}

export interface ProductFilters {
  category_id?: string;
  available?: boolean;
  search?: string;
  page?: number;
  per_page?: number;
}

export interface TableFilters {
  location?: string;
  occupied_only?: boolean;
  available_only?: boolean;
}

// KOT / Station Types
export interface KitchenStation {
  id: string;
  name: string;
  output_type: 'kds' | 'printer';
  /** Thermal slip: print at station vs checkout counter (hand off) */
  print_location?: 'kitchen' | 'counter';
  /** Physical printer for this station's slips (OS printer name or "ip:port"); empty => OS default. Desktop app only. */
  printer_target?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  category_ids?: string[];
}

export interface VoidLogEntry {
  id: string;
  order_id?: string;
  order_item_id?: string;
  voided_by?: string;
  authorized_by?: string;
  item_name: string;
  quantity: number;
  unit_price: number;
  reason?: string;
  created_at: string;
  order_number?: string;
  order_type?: string;
  table_number?: string;
  voided_by_name?: string;
  authorized_name?: string;
  /** F-CTRL-N74: absent on voids from before outcomes were recorded. */
  outcome?: 'not_made' | 'wasted';
  reason_code?: string;
  item_status_at_void?: string;
  /** Ingredient cost of a wasted void. */
  waste_cost?: number;
  discount_amount?: number;
}

/** Figures for the rows a void-log list call would return (F-CTRL-N68). */
export interface VoidLogSummary {
  count: number;
  total_value: number;
  self_authorized_count: number;
  other_count: number;
  top_reason?: { reason: string; count: number };
  top_voider?: { user_id: string; name: string; count: number; value: number };
  /** F-CTRL-N74: the split by outcome, and what the wasted food cost. */
  not_made_count?: number;
  wasted_count?: number;
  unclassified_count?: number;
  wasted_value?: number;
  waste_cost?: number;
}

/**
 * Wire shape for replaying an order taken while offline (F-CTRL-N34).
 *
 * `op_id` is the idempotency key — the backend stores it on the order behind a
 * unique index, so a retry is answered with `offline_order_duplicate` instead
 * of creating a second order. `captured_at` (epoch ms) is when the cashier
 * actually committed the order and is what the business day is resolved from;
 * the server refuses the request without it rather than defaulting to now.
 */
export interface OfflineReplayRequest {
  op_id: string;
  /**
   * The local ticket the offline kitchen slip printed, e.g. "OFF-A3-7"
   * (F-CTRL-N52). Recorded on the order so a slip in the pass can be matched to
   * the bill it became. NOT an order number — the server allocates that on
   * arrival, which is what keeps the daily sequence contiguous.
   */
  ticket_ref: string;
  /**
   * LEGACY: a number reserved before the outage by a pre-N52 bundle, already
   * printed on that terminal's slip. Omitted by this bundle. The server
   * validates it (format, reserved band, captured business day) before
   * honouring it — a client cannot name its own invoice number.
   */
  order_number?: string;
  captured_at: number;
  order_type: string;
  table_id: string | null;
  /**
   * Set when the operation APPENDS to an order that already exists on the
   * server (F-CTRL-N37). The replay engine routes these to the add-items
   * endpoint instead of creating a new order — the order is already there,
   * and creating a second one would double-bill the customer.
   */
  target_order_id?: string | null;
  notes?: string;
  /**
   * This client's statement that it could NOT print counter-bound slips itself
   * (F-FE-N124) — the same declaration every live `fireKOT` call makes. A
   * tablet till prints kitchen slips over the LAN but has no route to the USB
   * printer on the counter PC, so those slips printed nowhere during the
   * outage and must be queued for the counter on replay. The desktop till
   * sends false: its slips already printed, and a relayed job would be a
   * second ticket for food already served.
   */
  relay_counter_print?: boolean;
  /** Guests at the table (F-CTRL-N67). Floor entries only. */
  guest_count?: number;
  /**
   * Advisory: which screen took the order. The server derives provenance and
   * the dine-in lock from the caller's ROLE and never reads this field.
   */
  source?: 'pos' | 'server';
  /**
   * The tender ALREADY taken during the outage (F-MM-N26), so the server
   * prices the order at that rate rather than the cash placeholder and marks
   * it pay-intent-locked — which is exactly true, the customer paid and left.
   *
   * Absent when the order was fired but not paid. That case must stay unlocked
   * so the tender is chosen at the counter, and omitting the field is what
   * preserves it.
   */
  paid_with?: 'cash' | 'card' | 'online';
  lines: {
    product_id: string;
    quantity: number;
    special_instructions?: string;
    /**
     * Chosen modifier options, ids only. Re-resolved and re-priced server-side
     * at replay time against the live menu, like every other price on this
     * request.
     */
    modifiers?: CreateOrderItemModifier[];
  }[];
}

/**
 * An offline operation that was fired to the kitchen but could never be billed
 * (F-CTRL-N35) — the target order went terminal, or its table was taken, while
 * the terminal was dark. Awaits a manager's bill-or-write-off decision, and
 * blocks Day Close until it gets one.
 */
export interface UnreconciledOfflineItem {
  id: string;
  op_id: string;
  order_number: string;
  captured_at: string;
  lines: {
    productId: string;
    quantity: number;
    /**
     * The product's name (F-FE-N151). Snapshotted at capture on a current
     * bundle, otherwise resolved by the list endpoint's join. Still optional:
     * a line whose product was deleted has neither, and the UI must fall back
     * rather than render "undefined" at a manager.
     */
    name?: string;
    special_instructions?: string;
  }[];
  /** Stable code explaining why it could not post, e.g. order_not_editable. */
  failure_code: string;
  status: 'open' | 'billed' | 'written_off';
  resolved_note: string;
  /**
   * What the cashier COLLECTED offline, and what the order actually SETTLED at
   * (F-CTRL-N55). The backend has always sent both; this type omitted them, so
   * the two numbers that answer "how far off is the till, and which way" were
   * fetched and discarded — leaving the manager a raw failure code.
   *
   * `settled_amount` is absent when the payment never posted at all, which is a
   * different problem from one that posted at a different amount, and the UI
   * must say which.
   */
  collected_amount?: number;
  settled_amount?: number;
}

export interface StationKOT {
  station_id: string;
  station_name: string;
  output_type: 'kds' | 'printer';
  print_location?: 'kitchen' | 'counter';
  /** Physical printer to route this slip to (desktop app); empty => OS default. */
  printer_target?: string;
  /** Printer slips: the 32-col text (ESC/POS). KDS slips: the kds payload object. */
  payload: any;
  /**
   * Structured form of a printer slip (absent on KDS slips and on payloads from
   * a backend older than this field). The desktop/browser print path renders
   * HTML from it; without it the text payload prints in a <pre>.
   */
  slip?: KotSlip;
  /**
   * The backend queued this slip for the counter terminal to print (F-FE-N60),
   * because the client that fired cannot reach the counter's USB printer.
   *
   * A relayed slip MUST NOT be printed locally — the paper is already someone
   * else's job and printing it here too would double the ticket. Every branch of
   * printKotReceipts filters on this.
   */
  relayed?: boolean;
}

/** A slip the backend queued for the counter terminal, as returned to the firing client. */
export interface RelayedPrintJob {
  id: string;
  station_name: string;
}

export interface FireKOTResponse {
  kots: StationKOT[];
  /**
   * Optional-for-old-backends: a shell that predates F-FE-N60 returns no such
   * field, and a client that never asked to relay gets an empty list.
   */
  relayed_print_jobs?: RelayedPrintJob[];
}

// ── Counter print relay (F-FE-N60) ─────────────────────────────────────────

/** One relayed slip, as handed to the counter terminal's print agent. */
export interface PrintJob {
  id: string;
  order_id?: string;
  station_id?: string;
  station_name: string;
  kind: 'fire' | 'void' | 'reprint';
  slip: KotSlip | null;
  /** 32-col text form; used when `slip` is absent. */
  payload_text: string;
  printer_target?: string;
  attempts: number;
}

/**
 * Lifecycle of a relayed slip. 'expired' (F-BE-N79): not printed within 10
 * minutes, so it never will be — late paper is worse than none.
 */
export type PrintJobState = 'pending' | 'claimed' | 'printed' | 'failed' | 'expired';

/** Status of a relayed slip, for the tablet that queued it. */
export interface PrintJobStatus {
  id: string;
  station_name: string;
  status: PrintJobState;
  attempts: number;
  last_error?: string;
}

/**
 * GET /print-jobs/status payload. The station fields are optional because a
 * backend older than F-BE-N79 does not send them — absence means "unknown",
 * never "offline".
 */
export interface PrintJobStatusResponse {
  jobs: PrintJobStatus[];
  /** Seconds since any print station last polled; null = none since the backend started. */
  station_last_seen_seconds?: number | null;
  /** Seconds the backend has been up and watching for a station. */
  station_tracking_seconds?: number;
}

/** Admin audit row for the open business day. */
export interface PrintJobAudit {
  id: string;
  station_name: string;
  kind: 'fire' | 'void' | 'reprint';
  status: PrintJobState;
  attempts: number;
  claimed_by?: string;
  created_at: string;
  printed_at?: string;
  last_error?: string;
  order_number?: string;
}

// ───────────────────────────────────────────────────────────────────────────
// Fiscal Credit / Debit Notes
// ───────────────────────────────────────────────────────────────────────────

export interface FiscalNoteItem {
  product_name: string;
  /** Present on notes written after per-item loyalty landed; absent on older
   *  ones and on the service-charge / delivery fee lines, which aren't
   *  merchandise. Lets the backend re-grade loyalty stamps by product rather
   *  than by (ambiguous) name. */
  product_id?: string;
  quantity: number;
  unit_price: number;
  total: number;
  pct_code: string;
}

export interface FiscalNote {
  id: string;
  order_id: string;
  note_type: 'credit' | 'debit';
  /** Null for non-fiscal refunds (tenant with no FBR jurisdiction configured). */
  ref_invoice_number: string | null;
  ref_usin?: string | null;
  items: FiscalNoteItem[];
  subtotal: number;
  tax_amount: number;
  total_amount: number;
  fiscal_details?: FiscalDetails;
  reason?: string | null;
  status: 'draft' | 'pending' | 'synced' | 'failed';
  created_by_name?: string | null;
  created_at: string;
}

export interface CreateFiscalNoteRequest {
  order_id: string;
  note_type: 'credit' | 'debit';
  reason: string;
  pin?: string;
  items: {
    product_name: string;
    /** Optional on the wire, but send it: it's how the backend re-grades
     *  loyalty stamps for the refunded lines specifically. */
    product_id?: string;
    quantity: number;
    unit_price: number;
    pct_code?: string;
  }[];
  /** F-CTRL-N74: per refunded line, put the stock back or record it as wasted. */
  stock?: CreditNoteStockAction[];
}

/** One refunded line's stock decision (F-CTRL-N74). */
export interface CreditNoteStockAction {
  order_item_id: string;
  quantity: number;
  action: 'restock' | 'wasted';
  reason_code?: string;
}

export interface IssueCreditNoteRequest {
  pin: string;
  reason: string;
  /** F-CTRL-N74: lines not listed as wasted are put back in stock. */
  stock?: CreditNoteStockAction[];
}

export interface FiscalAuditEvent {
  id: string;
  order_id?: string | null;
  order_number?: string | null;
  workflow: string;
  phase: string;
  outcome: string;
  http_status?: number | null;
  latency_ms?: number | null;
  error_code?: string | null;
  request_excerpt?: string | null;
  response_excerpt?: string | null;
  created_at: string;
}

// One row of the live fiscal_outbound_jobs queue — the current submission state
// for an order's FBR/PRAL workflow (pending/processing/succeeded/dead/cancelled),
// backing the "needs attention" section of the Fiscal Queue tab.
export interface FiscalJob {
  id: string;
  order_id: string;
  order_number?: string | null;
  total_amount?: number | null;
  workflow: string;
  status: 'pending' | 'processing' | 'succeeded' | 'dead' | 'cancelled';
  attempt_count: number;
  max_attempts: number;
  last_error?: string | null;
  next_run_at?: string | null;
  created_at: string;
  updated_at: string;
  succeeded_at?: string | null;
}

/**
 * One PRA submission whose outcome is genuinely unknown.
 *
 * PRA is not idempotent — the same USIN posted repeatedly yields a new invoice
 * number each time — and it exposes no query-by-USIN endpoint. So when a POST
 * fails without a readable response, the system cannot tell whether PRA filed
 * the invoice, and it parks the claim rather than risk a duplicate filing.
 * Mirrors handlers.PRAUnresolvedRow.
 */
export interface PRAUnresolvedClaim {
  id: string;
  order_id?: string | null;
  order_number?: string | null;
  note_id?: string | null;
  workflow: string;
  usin: string;
  posid: number;
  attempt_count: number;
  last_error?: string | null;
  claimed_at: string;
  order_total?: number | null;
  verify_hint: string;
}

// Marketing QR (Admin Settings) — Zod-inferred; keep in sync with backend marketing.qr_codes
export type { MarketingQrCodeItem, MarketingQrCodesPayload } from '@/lib/form-schemas'

// Dynamic marketing QR — one row per slot, with the immutable redirect
// slug printed inside the PNG and rolling scan analytics computed on read.
export interface MarketingQRStyle {
  dark_color: string
  light_color: string
  margin_modules: number
  dots_type: string
  corner_square_type: string
  corner_dot_type: string
  error_correction: string
  logo_enabled?: boolean
  logo_scale?: number
}

export interface MarketingQRDeviceMix {
  ios: number
  android: number
  desktop: number
  other: number
}

export interface MarketingQRCodeRow {
  id: string
  slug: string
  slot_index: number
  target_url: string
  label: string
  style: MarketingQRStyle
  is_active: boolean
  scan_count_total: number
  scan_count_last_30d: number
  unique_visitors_last_30d: number
  last_scanned_at?: string | null
  device_mix_last_30d: MarketingQRDeviceMix
}

// 'db'   — admin saved a value in the settings UI
// 'env'  — MARKETING_QR_REDIRECT_BASE env var on the backend
// 'auto' — neither was set; backend fell back to the request's own host
//          (fine for local dev, fragile for anything printed)
export type MarketingQRRedirectBaseSource = 'db' | 'env' | 'auto'

export interface MarketingQRListResponse {
  redirect_base: string
  source: MarketingQRRedirectBaseSource
  items: MarketingQRCodeRow[]
}

export interface MarketingQRTimeseriesBucket {
  day: string
  scans: number
  uniques: number
}

export interface MarketingQRTimeseriesTotals {
  scans: number
  unique_visitors: number
  device_mix: MarketingQRDeviceMix
}

export interface MarketingQRTimeseriesResponse {
  days: number
  timezone: string
  buckets: MarketingQRTimeseriesBucket[]
  totals: MarketingQRTimeseriesTotals
}

export interface UpdateMarketingQRRequest {
  target_url?: string
  label?: string
  style?: MarketingQRStyle
  is_active?: boolean
}

// Body is optional on the wire — empty POST mints a QR with a default
// "QR N" label. Sending a non-empty label seeds it without a follow-up
// PUT (useful for the empty-state "Create your first QR" flow).
export interface CreateMarketingQRRequest {
  label?: string
}

// Minimal slug-and-label echo from POST /admin/marketing/qr. The full
// row arrives via the next list refetch — we don't bother re-deriving
// it from one create call.
export interface CreateMarketingQRResponse {
  id: string
  slug: string
  label: string
}

// Poster Studio. Lives in app_settings as marketing.poster_config —
// fetched/saved via the generic settings endpoints. There's no
// dedicated handler; the client is the renderer.
export type PosterPurpose =
  | 'loyalty'
  | 'menu'
  | 'reviews'
  | 'instagram'
  | 'website'
  // Dine-in table QR (F-FE-N100). Its own purpose rather than a reuse of
  // 'menu': the printed artefact is a per-table tent carrying a per-table
  // slug, so the copy, the layout and the destination all differ.
  | 'dine_in'
  | 'custom'

export type PosterThemeId =
  | 'warm-saffron'
  | 'midnight-ember'
  | 'cream-minimal'
  | 'brutalist-bold'
  | 'editorial-serif'
  | 'terracotta-glow'

export type PosterLayout = 'a4' | 'tent' | 'card'
export type PosterLogoSize = 'sm' | 'md' | 'lg' | 'xl'

export interface PosterConfig {
  themeId: PosterThemeId
  layout: PosterLayout
  logoSize: PosterLogoSize
  /** Hex override (#RRGGBB). null/empty uses theme default. */
  accentOverride: string | null
  headline: string
  subhead: string
  footer: string
  qrCaption: string
  /** Show the three-step "how it works" strip (useful for loyalty). */
  showExplainer: boolean
  /** Destination URL encoded into the printed QR. Often points at a
   *  /q/<slug> redirect set up in Marketing QR; can also be direct. */
  qrTargetUrl: string
}

export type MarketingPosterConfig = Record<PosterPurpose, PosterConfig>

// ───────────────────────────────────────────────────────────────────────────
// Marketing Suite — /admin/marketing/overview payload types.
// ───────────────────────────────────────────────────────────────────────────

export interface MarketingKPIIntPair extends IntMetricPair {
  sparkline: number[]
}

export interface MarketingKPIRevenuePair extends MetricPair {
  sparkline: number[]
}

export interface MarketingReachableCustomers {
  with_phone: number
  with_email: number
  total: number
}

export interface MarketingOverviewKPIs {
  promo_redemptions: MarketingKPIIntPair
  promo_revenue_pkr: MarketingKPIRevenuePair
  loyalty_rewards: MarketingKPIIntPair
  qr_scans: MarketingKPIIntPair
  new_customers: MarketingKPIIntPair
  reachable_customers: MarketingReachableCustomers
}

export interface MarketingCustomerMix {
  new: number
  active: number
  at_risk: number
  lapsed: number
  vip: number
  unknown: number
}

export interface MarketingEngagementDay {
  date: string
  promo: number
  loyalty: number
  qr: number
}

export interface MarketingTopPromo {
  id: string
  code: string
  redemptions: number
  revenue_pkr: number
}

export interface MarketingTopQR {
  id: string
  label: string
  slug: string
  scans: number
}

export interface MarketingTopPerformers {
  best_promo?: MarketingTopPromo
  top_qr?: MarketingTopQR
}

export interface MarketingExpiringPromo {
  id: string
  code: string
  expires_at: string
}

export interface MarketingStaleQR {
  id: string
  label: string
  slug: string
}

export interface MarketingNeedsAttention {
  promos_expiring_soon: MarketingExpiringPromo[]
  lapsed_customers: number
  stale_qrs: MarketingStaleQR[]
  loyalty_disabled: boolean
}

export interface MarketingOccasion {
  customer_id: string
  name?: string
  date: string
  phone_normalized?: string
  email?: string
  kind: string
}

export interface MarketingActivityEvent {
  kind: 'punch' | 'reward' | 'promo_redemption' | 'qr_scan' | string
  label: string
  at: string
}

export interface MarketingChannelStatus {
  loyalty_enabled: boolean
  loyalty_threshold: number
  active_promos: number
  qr_codes: number
  campaigns_sent: number
}

export interface MarketingOverviewWindow {
  from: string
  to: string
  previous_from: string
  previous_to: string
  days: number
}

export interface MarketingOverviewResponse {
  window: MarketingOverviewWindow
  kpis: MarketingOverviewKPIs
  customer_mix: MarketingCustomerMix
  engagement_daily: MarketingEngagementDay[]
  top_performers: MarketingTopPerformers
  needs_attention: MarketingNeedsAttention
  occasions_this_week: MarketingOccasion[]
  recent_activity: MarketingActivityEvent[]
  channel_status: MarketingChannelStatus
}

// Aging open-orders alert (GET /admin/day-ops/aging-open-orders) — real-value
// orders left open far too long, surfaced so they're chased in days not months.
export interface AgingOpenOrder {
  order_number: string
  order_type: string
  status: string
  total_amount: number
  age_hours: number
  table_label: string
  created_at: string
}

export interface AgingOpenOrdersResult {
  threshold_hours: number
  count: number
  total_amount: number
  orders: AgingOpenOrder[]
}

// Tender reconciliation (GET /admin/day-ops/days/:id/tender-reconciliation) —
// card/online payments swiped on this day but booked to earlier days, so a
// card-machine / bank-total variance at close is explained.
export interface CrossDayTender {
  order_number: string
  method: string
  amount: number
  sale_date: string
  paid_date: string
}
export interface TenderReconResult {
  business_date: string
  card_cross_day: number
  online_cross_day: number
  items: CrossDayTender[]
}
